'use client';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { PlotlyChartProps, Threshold, VerticalLine, CustomLinesConfig } from '../../types';
import Plot from 'react-plotly.js';
import type { ModeBarDefaultButtons } from 'plotly.js';
import { ChartEmptyState, EMPTY_STATE_ENUM } from './ChartEmptyState';
import { ChartPopover } from './ChartPopover';
import { CustomLineTooltip } from '../dialogs/CustomLineTooltip';
import { CustomVerticalLineMenu } from '../dialogs/CustomVerticalLineMenu';
import { useCustomVerticalLines } from '@/hooks/shared/DataLayers/useCustomVerticalLines';
import { useDarkMode } from '@/contexts/DarkModeContext';
import { useAuth } from '@/providers/AuthProvider';
import { FloatingChartToolbar } from '../toolbar/FloatingChartToolbar';
import { BarChart3 } from 'lucide-react';
import { generateTestId } from '@/utils/shared/generateTestIds';
import { useDrawingContextOptional, DrawingCanvas } from '../drawing';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import {
  buildEvidenceMarkers,
  buildLevelShapes as buildVisualAILevelShapes,
  getCombinedYRangeForVisualAI,
} from './PlotlyChartVisualAIUtils';

// Custom date formatter to match "Jan 1th, 2025 20:00" format
const formatCustomDate = (date: Date): string => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate();
  const ordinal =
    day === 1 || day === 21 || day === 31
      ? 'st'
      : day === 2 || day === 22
      ? 'nd'
      : day === 3 || day === 23
      ? 'rd'
      : 'th';

  return `${months[date.getMonth()]} ${day}${ordinal}, ${date.getFullYear()} ${date
    .getHours()
    .toString()
    .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
};

interface ExtendedPlotlyChartProps extends PlotlyChartProps {
  preservedTimeRange?: [Date, Date] | null;
  isModeTransition?: boolean;
  thresholds: Threshold[];
  sensorName?: string;
  chartType: string;
  sectionId?: string;
  dataLayer?: string;
  onAddThreshold?: (sensorName: string, t: Omit<Threshold, 'id'>) => void;
  onRemoveThreshold?: (sensorName: string, id: string) => void;
  onUpdateThreshold?: (sensorName: string, id: string, updates: Partial<Threshold>) => void;
  onHarmonicLineClick?: (lineId: string, position: { x: number; y: number }) => void;
  onVerticalLineClick?: (lineId: string, position: { x: number; y: number }, clickedChartId?: string) => void;
  onCustomLineDoubleClick?: (lineData: any, position: { x: number; y: number }) => void; // Custom double-click handler
  focusedHarmonicColor?: string | null;
  benchmarkLineId?: string | null;
  selectedHarmonicLineId?: string | null;
  focusedSensor?: string | { sensorName: string; sectionId: string } | null;
  markerLabel?: string; // Custom label for marker mode (e.g., "Timestamps" for sensors)
  // shapeLevels?: ShapeLevels;
  customVerticalLines?: any;
  isCustomLineMode?: string | null;
  onEnterCustomLineMode?: (groupId: string) => void;
  setCustomLineMenuState?: (state: any) => void;
  focusedCustomGroupId?: string | null;
  customLinesConfig?: CustomLinesConfig; // Configuration for custom lines behavior
}

const PlotlyChartComponent = ({
  data,
  title,
  unit,
  height,
  isDiscrete,
  colors,
  labels,
  enabledSensors,
  verticalLines,
  sectionId,
  dataLayer,
  thresholds,
  chartType,
  sensorName,
  onAddVerticalLine,
  onRemoveVerticalLine,
  onUpdateVerticalLine,
  chartId,
  sharedTimeRange,
  onRangeChange,
  onEditThresholds,
  showThresholds = false,
  thresholdVariant = 'bands-fill',
  intervalLines,
  onRemoveIntervalLine,
  selectionMode,
  selectedRanges,
  onAddSelectedRange,
  onRemoveSelectedRange,
  globalYRange,
  showUnits,
  isComparisonEnabled,
  splitColumns,
  showZeroInTooltip = true,
  showTooltipData = false,
  renderTooltipData,
  preservedTimeRange = null,
  isModeTransition = false,
  isMarkerSelectionMode,
  isHarmonicSelectionMode,
  onAddHarmonicSelection,
  isSideBandsSelectionMode,
  onGenerateSideBandsFromClick,
  onHarmonicLineClick,
  onVerticalLineClick,
  onCustomLineDoubleClick,
  focusedHarmonicColor,
  benchmarkLineId,
  selectedHarmonicLineId,
  focusedSensor = null,
  pendingHarmonicCreation,
  onCancelPendingHarmonic,
  showDualLabels = true,
  showComparisonTable = false,
  enableDoubleClickZoomReset = false,
  selectedMarkerLabel,
  selectedTimestampsSet,
  markerLabel = 'Markers',
  customVerticalLines,
  isCustomLineMode,
  onEnterCustomLineMode,
  setCustomLineMenuState,
  focusedCustomGroupId,
  customLinesConfig,
  // Floating toolbar props
  onEnterMarkerSelectionMode,
  onEnterHarmonicSelectionMode,
  onEnterSideBandsSelectionMode,
  harmonicSelections = [],
  sideBandsSelections = [],
  onUpdateHarmonicSelection,
  onRemoveHarmonicSelection,
  onRemoveAllHarmonics,
  onFocusHarmonic,
  onResetHarmonic,
  onGenerateHarmonics,
  onToggleCompareModal,
  onClearLines,
  showFloatingToolbar = false,
  markingTools = {
    markers: true,
    harmonics: true,
    sideBands: true,
    measure: true,
  },
  expandableLegendSeries = [],
  expandableLegendFocusMode = false,
  waveformPlaybackTime,
  waveformDuration,
  onWaveformSeek,
}: ExtendedPlotlyChartProps) => {
  // Extract custom lines configuration with defaults
  const syncCustomLines = customLinesConfig?.sync ?? true;
  const enableDynamicCustomLineButtons = customLinesConfig?.enableDynamicButtons ?? false;
  const showCreatorLabel = customLinesConfig?.showCreatorLabel ?? false;
  const disableCustomLinePopoverMenu = customLinesConfig?.disablePopoverMenu ?? true;
  const disableCustomLineHoverTooltip = customLinesConfig?.disableHoverTooltip ?? true;

  // Check if this is a spectrum chart (frequency-based x-axis) - must be defined early
  const isSpectrum =
    dataLayer === 'spectrum_analysis' ||
    dataLayer === 'spectrum' ||
    chartType === 'spectrum_analysis' ||
    chartType === 'spectrum' ||
    sectionId?.toLowerCase().includes('spectrum');
  // Check if this is a Visual AI chart
  const isVisualAI = dataLayer === 'visualAI' || dataLayer === 'beltMisalignment' || dataLayer === 'rockSize';

  // Check if this is a waveform chart (time-based x-axis with mm:ss format)
  const isWaveform = chartType === 'waveform' || dataLayer === 'waveform';

  // Normalize focusedSensor to handle both string and object types
  // Also check if the focus belongs to this section
  const normalizedFocusedSensor = React.useMemo(() => {
    if (!focusedSensor) return null;

    // If it's a string, focus applies to all sections (legacy behavior)
    if (typeof focusedSensor === 'string') return focusedSensor;

    // If it's an object, only apply focus if it belongs to this section
    if (focusedSensor.sectionId && focusedSensor.sectionId !== sectionId) {
      return null; // Different section, don't apply focus
    }

    return focusedSensor.sensorName;
  }, [focusedSensor, sectionId]);

  // Debug focus sensor
  React.useEffect(() => {}, [focusedSensor, normalizedFocusedSensor, chartId, sectionId, labels]);

  // This will be moved after state declarations

  const [isLoaded, setIsLoaded] = useState(false);
  const [originalYRange, setOriginalYRange] = useState<[number, number] | null>(null);
  const [renderTime, setRenderTime] = useState(0);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const renderStartTime = useRef<number>(0);
  const [minY, setMinY] = useState(Number.POSITIVE_INFINITY);
  const [maxY, setMaxY] = useState(Number.NEGATIVE_INFINITY);
  const plotRef = useRef<any>(null);
  const clickCountRef = useRef(0);
  const lastClickTime = useRef<number>(0);
  const pendingClickTimeout = useRef<NodeJS.Timeout | null>(null);
  const initialXRangeRef = useRef<[number, number] | null>(null); // Store initial X-axis range
  const verticalLinesEnabled = isComparisonEnabled; // Enable vertical lines when comparison is enabled

  // State for pending harmonic/side bands creation hover indicator
  const [hoverXPosition, setHoverXPosition] = useState<number | null>(null);

  // State for cursor label (shows active marking mode)
  const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null);

  // State for custom vertical lines (manage user-added lines)
  const [customVerticalLinesState, setCustomVerticalLinesState] = useState<any>(() => {
    // Initialize with the prop data if available
    return customVerticalLines || { groups: [] };
  });

  // State for custom line menu
  const [customLineMenu, setCustomLineMenu] = useState<{
    isOpen: boolean;
    lineData: any;
    position: { x: number; y: number };
    chartId?: string;
  } | null>(null);

  // State for custom line tooltip (double-click)
  const [customLineTooltip, setCustomLineTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    lineData: any;
  }>({ visible: false, x: 0, y: 0, lineData: null });

  // State for double-click detection on custom lines
  const [customLastClickedLineId, setCustomLastClickedLineId] = useState<string | null>(null);

  // State for focused custom group (like spectrum focus mode)
  const [localFocusedCustomGroupId, setLocalFocusedCustomGroupId] = useState<string | null>(
    focusedCustomGroupId || null
  );

  // Dynamic state for custom vertical line groups and modes
  const [customLineGroups, setCustomLineGroups] = useState<{
    [groupId: string]: {
      groupId: string;
      groupLabel: string;
      groupIcon: string;
      groupColor: string;
      visible: boolean;
      isMode: boolean; // Whether this group is currently in "add mode"
      itemCount: number;
    };
  }>({});

  // Current active custom line mode
  const [activeCustomLineMode, setActiveCustomLineMode] = useState<string | null>(null);

  // Dynamically update custom line groups from API data (only if enabled)
  useEffect(() => {
    if (enableDynamicCustomLineButtons && customVerticalLines?.groups) {
      const newGroups: typeof customLineGroups = {};

      customVerticalLines.groups.forEach((group: any) => {
        newGroups[group.groupId] = {
          groupId: String(group.groupId),
          groupLabel: String(group.groupLabel),
          groupIcon: String(group.groupIcon),
          groupColor: String(group.groupColor || '#6366f1'),
          visible: Boolean(group.visible !== false),
          isMode: false, // Initially not in mode
          itemCount: Number(group.groupItems?.length || 0),
        };
      });

      setCustomLineGroups(newGroups);
    } else if (!enableDynamicCustomLineButtons) {
      // Clear groups if dynamic buttons are disabled
      setCustomLineGroups({});
    }
  }, [customVerticalLines, enableDynamicCustomLineButtons]);

  // Functions to handle dynamic custom line modes
  const enterCustomLineMode = useCallback(
    (groupId: string) => {
      setActiveCustomLineMode(groupId);
      setCustomLineGroups((prev) => ({
        ...prev,
        [groupId]: {
          ...prev[groupId],
          isMode: true,
        },
      }));

      // Exit other modes
      Object.keys(customLineGroups).forEach((id) => {
        if (id !== groupId && customLineGroups[id].isMode) {
          exitCustomLineMode(id);
        }
      });
    },
    [customLineGroups]
  );

  const exitCustomLineMode = useCallback(
    (groupId?: string) => {
      if (groupId) {
        setCustomLineGroups((prev) => ({
          ...prev,
          [groupId]: {
            ...prev[groupId],
            isMode: false,
          },
        }));
        if (activeCustomLineMode === groupId) {
          setActiveCustomLineMode(null);
        }
      } else {
        // Exit all modes
        setActiveCustomLineMode(null);
        setCustomLineGroups((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((id) => {
            updated[id] = { ...updated[id], isMode: false };
          });
          return updated;
        });
      }
    },
    [activeCustomLineMode]
  );

  const addLineToCustomGroup = useCallback(
    (groupId: string, lineData: any) => {
      // Create a completely mutable copy of lineData
      const mutableLineData = JSON.parse(JSON.stringify(lineData));

      // Update the group's item count
      setCustomLineGroups((prev) => ({
        ...prev,
        [groupId]: {
          ...prev[groupId],
          itemCount: prev[groupId].itemCount + 1,
        },
      }));

      // Call the original onAddVerticalLine with custom group info
      if (onAddVerticalLine) {
        try {
          onAddVerticalLine(
            new Date(mutableLineData.timestamp),
            Object.create(mutableLineData.values || {}),
            mutableLineData.clickedXValue,
            String(`${mutableLineData.label}|${groupId}|👤 ${mutableLineData.username}`),
            String(customLineGroups[groupId]?.groupColor || '#6366f1'),
            false,
            false,
            syncCustomLines ? undefined : String(chartId),
            String(sectionId),
            'user' as const,
            undefined,
            String(groupId)
          );
        } catch (error) {
          console.error('Failed to add vertical line:', error);
        }
      }
    },
    [customLineGroups, onAddVerticalLine, chartId, sectionId, syncCustomLines]
  );

  // Combine regular vertical lines with custom vertical lines
  const allVerticalLines = useMemo(() => {
    const regularLines = verticalLines || [];

    // Use state-based custom lines if they exist, otherwise fall back to props
    const effectiveCustomVerticalLines = customVerticalLinesState?.groups
      ? customVerticalLinesState
      : customVerticalLines;

    if (!effectiveCustomVerticalLines?.groups || !Array.isArray(effectiveCustomVerticalLines.groups)) {
      return regularLines;
    }

    const customLines: VerticalLine[] = [];

    effectiveCustomVerticalLines.groups.forEach((group: any) => {
      if (!group.visible || !group.groupItems || !Array.isArray(group.groupItems)) return;

      group.groupItems.forEach((item: any) => {
        // Filter lines based on sync setting and chartId
        // If synced (true), show all lines regardless of chartId
        // If not synced (false), only show lines for this specific chart
        if (syncCustomLines) {
          // When synced, ignore chartId completely - show all lines on all charts
        } else {
          // When not synced, only show lines that match this chart
          if (item.chartId && item.chartId !== chartId) {
            return; // Skip this line as it belongs to a different chart
          }
        }

        // Extract the ACTUAL username that's displayed on the chart
        let extractedUsername = null;
        let cleanLabel = item.label || group.groupLabel;

        // The username is showing on the chart, check all possible locations:
        // 1. Direct fields
        extractedUsername = item.username || item.addedBy || item.user || item.createdBy;

        // 2. If stored in label format (from onAddVerticalLine)
        if (!extractedUsername && item.label && item.label.includes('|')) {
          const parts = item.label.split('|');
          if (parts.length >= 3) {
            cleanLabel = parts[0];
            const userPart = parts[2];
            if (userPart) {
              extractedUsername = userPart.replace('👤 ', '').trim();
            }
          }
        }

        // 3. Check the value field - this is what shows on the x-axis label
        if (!extractedUsername && item.value) {
          // The value IS the username that's displayed
          extractedUsername = item.value;
          // Don't use value as label if it's the username
          if (cleanLabel === item.value) {
            cleanLabel = group.groupLabel;
          }
        }

        const customLine: VerticalLine = {
          // Spread all original item data first to preserve everything
          ...item,
          // Then override with processed values
          id: item.id || `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: item.x || item.timestamp || item.value,
          label: cleanLabel,
          values: item.values || {},
          color: '#55687d', // Gray color for custom lines
          isCustomLine: true,
          markerType: 'custom', // Explicitly set markerType to prevent map pins
          _systemGroupId: group.groupId,
          _systemGroupLabel: group.groupLabel,
          _systemLineType: 'custom',
          _systemGroupIcon: item.groupIcon || group.groupIcon,
          groupIcon: item.groupIcon || group.groupIcon,
          groupId: group.groupId,
          source: item.source || 'user', // Default to 'user' not 'system' - most lines are user-added
          username: extractedUsername,
          displayLabel: cleanLabel || group.groupLabel,
          chartId: item.chartId, // Store the chartId for reference
          // Media and content fields
          mediaUrl: item.mediaUrl || group.mediaUrl,
          thumbnailUrl: item.thumbnailUrl || group.thumbnailUrl,
          mediaType: item.mediaType || group.mediaType || 'image',
          description: item.description || group.description,
          title: item.title || `${item.groupIcon || group.groupIcon} ${group.groupLabel}`,
          // Keep ALL original fields
          _originalItem: item,
        };
        customLines.push(customLine);
      });
    });

    return [...regularLines, ...customLines];
  }, [verticalLines, customVerticalLines, customVerticalLinesState, syncCustomLines, chartId]);

  // Check if this chart is in pending mode (waiting for second click)
  const isPendingMode = pendingHarmonicCreation?.chartId === chartId;

  // Get dark mode state early
  const { isDarkMode } = useDarkMode();

  // Get drawing context to check if drawing mode is active
  const drawingContext = useDrawingContextOptional();
  const isDrawingActive = drawingContext?.state?.activeTool !== null && drawingContext?.state?.activeTool !== undefined;

  // Debug pending harmonic creation state
  React.useEffect(() => {
    if (isPendingMode) {
    } else if (pendingHarmonicCreation) {
    }
  }, [isPendingMode, chartId, pendingHarmonicCreation]);

  // Track mouse movement on chart for purple hover line (in pending mode)
  React.useEffect(() => {
    if (!isPendingMode) {
      setHoverXPosition(null);
      return;
    }

    const chartDiv = document.getElementById(`chart-${chartId}`);
    if (!chartDiv) return;

    const handleMouseMove = (e: MouseEvent) => {
      try {
        // Get the plot div and its bounding box
        const plotDiv = chartDiv.querySelector('.plotly') as HTMLElement;
        if (!plotDiv) return;

        const rect = plotDiv.getBoundingClientRect();
        const x = e.clientX - rect.left;

        // Convert pixel position to data coordinates
        // This is approximate - Plotly has the exact conversion internally
        const plotWidth = rect.width;

        // Get x-axis range from the layout
        const plotlyDiv = chartDiv.querySelector('.js-plotly-plot') as any;
        if (!plotlyDiv || !plotlyDiv.layout || !plotlyDiv.layout.xaxis) return;

        const xaxis = plotlyDiv.layout.xaxis;
        const xRange = xaxis.range || [0, 100];
        const xMin = xRange[0];
        const xMax = xRange[1];

        // Convert pixel to data coordinate
        const xData = xMin + (x / plotWidth) * (xMax - xMin);

        setHoverXPosition(xData);
      } catch (error) {}
    };

    const handleMouseLeave = () => {
      setHoverXPosition(null);
    };

    chartDiv.addEventListener('mousemove', handleMouseMove);
    chartDiv.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      chartDiv.removeEventListener('mousemove', handleMouseMove);
      chartDiv.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isPendingMode, chartId]);

  // Use the custom vertical lines hook
  const { handleCustomLineClick } = useCustomVerticalLines({
    isCustomLineMode,
    customVerticalLines,
    onEnterCustomLineMode,
    verticalLines, // Pass vertical lines for counting existing custom lines
    onAddAnimation: (animationType: 'unicorn' | 'teddy', mouseX: number, mouseY: number) => {
      // Add animation to state
      const newAnimation = {
        id: Date.now(),
        type: animationType === 'unicorn' ? ('rainbow' as const) : ('heart' as const),
        x: mouseX,
        y: mouseY,
      };
      setAnimations((prev) => [...prev, newAnimation]);

      // Remove animation after 1.5 seconds
      setTimeout(() => {
        setAnimations((prev) => prev.filter((a) => a.id !== newAnimation.id));
      }, 1500);
    },
    onAddVerticalLine: (
      lineValue: any,
      values: any,
      clickedXValue: any,
      label?: string,
      color?: string,
      isHarmonic?: boolean,
      isFirstHarmonic?: boolean,
      chartId?: string,
      sectionKey?: string,
      source?: 'user' | 'system'
    ) => {
      // Call the original callback
      if (onAddVerticalLine) {
        onAddVerticalLine(
          lineValue,
          values,
          clickedXValue,
          label,
          color,
          isHarmonic,
          isFirstHarmonic,
          chartId,
          sectionKey,
          source
        );
      }

      // Update the custom vertical lines state if this is a custom line
      if (isCustomLineMode && label?.includes('|')) {
        const [lineLabel, groupId, username] = label.split('|');
        setCustomVerticalLinesState((prevState: any) => {
          // Deep clone the state to avoid mutation issues
          const newState = {
            ...prevState,
            groups: prevState.groups.map((group: any) => ({
              ...group,
              groupItems: [...(group.groupItems || [])],
            })),
          };

          // Find the group and add the new line item
          const groupIndex = newState.groups.findIndex((g: any) => g.groupId === groupId);
          if (groupIndex !== -1) {
            const newItem = {
              id: `user-${Date.now()}`,
              value: lineValue instanceof Date ? lineValue : new Date(lineValue),
              label: lineLabel,
              source: 'user',
              color: color || '#55687d',
              yValues: values,
              username: username?.replace('👤 ', '') || '',
            };

            newState.groups[groupIndex].groupItems.push(newItem);
          }

          return newState;
        });
      }
    },
    chartId,
    sectionKey: sectionId,
    showCreatorLabel,
  });

  // State for animations
  const [animations, setAnimations] = useState<Array<{ id: number; type: 'heart' | 'rainbow'; x: number; y: number }>>(
    []
  );

  // State for custom tooltip
  const [customTooltipData, setCustomTooltipData] = useState<{
    visible: boolean;
    position: { x: number; y: number };
    lineData: any;
  }>({
    visible: false,
    position: { x: 0, y: 0 },
    lineData: null,
  });

  // Track cursor position for mode label - only show when mouse is moving
  React.useEffect(() => {
    const isAnyModeActive =
      isMarkerSelectionMode ||
      isHarmonicSelectionMode ||
      isSideBandsSelectionMode ||
      isPendingMode ||
      isCustomLineMode ||
      activeCustomLineMode;

    if (!isAnyModeActive) {
      setCursorPosition(null);
      return;
    }

    const chartDiv = document.getElementById(`chart-${chartId}`);
    if (!chartDiv) return;

    let hideTimeout: NodeJS.Timeout;

    const handleMouseMove = (e: MouseEvent) => {
      // Convert viewport coordinates to container-relative coordinates
      const rect = chartDiv.getBoundingClientRect();
      setCursorPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });

      // Clear previous timeout
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }

      // Hide badge after 2 seconds of no mouse movement
      hideTimeout = setTimeout(() => {
        setCursorPosition(null);
      }, 2000);
    };

    const handleMouseLeave = () => {
      // Hide badge when mouse leaves chart
      setCursorPosition(null);
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };

    chartDiv.addEventListener('mousemove', handleMouseMove);
    chartDiv.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      chartDiv.removeEventListener('mousemove', handleMouseMove);
      chartDiv.removeEventListener('mouseleave', handleMouseLeave);
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [
    isMarkerSelectionMode,
    isHarmonicSelectionMode,
    isSideBandsSelectionMode,
    isPendingMode,
    isCustomLineMode,
    activeCustomLineMode,
    chartId,
  ]);

  // Debug focus
  React.useEffect(() => {
    if (focusedHarmonicColor) {
    }
  }, [focusedHarmonicColor, chartId]);

  // Debug benchmark
  React.useEffect(() => {}, [benchmarkLineId, chartId, verticalLines.length]);

  // Cache CSS color values to avoid repeated DOM reads
  const lineDefaultColor = React.useMemo(() => {
    const rootStyles = getComputedStyle(document.documentElement);
    return rootStyles.getPropertyValue('--color-line-default').trim() || '#4b5563';
  }, []);

  //track internal state to force proper range application
  const [internalTimeRange, setInternalTimeRange] = useState<[Date, Date] | null>(null);
  const hasAppliedPreservedRangeRef = useRef<boolean>(false);
  const rangeApplicationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  //Track chart component ID
  const chartComponentIdRef = useRef(Math.random().toString(36).substr(2, 9));
  const previousDataRef = useRef<string>('');
  const previousEnabledSensorsRef = useRef<string>('');

  // Debug isComparisonEnabled on every render
  React.useEffect(() => {}, [isComparisonEnabled, chartId, verticalLinesEnabled, selectionMode, onAddVerticalLine]);

  // Popover state
  const [popoverData, setPopoverData] = useState<{
    isVisible: boolean;
    position: { x: number; y: number };
    data: {
      timestamp: string;
      values: Array<{
        name: string;
        value: number;
        color: string;
        unit: string;
        tooltipData?: Record<string, any>;
      }>;
    } | null;
  }>({
    isVisible: false,
    position: { x: 0, y: 0 },
    data: null,
  });

  // Detect x-axis type from actual data
  const xAxisType: 'date' | 'numeric' | 'category' = React.useMemo(() => {
    // CRITICAL: Detect from actual x-axis data values first
    // This is more reliable than relying on props which may not be passed correctly

    // Check if we have valid data to inspect
    if (!data?.data || !Array.isArray(data.data) || data.data.length === 0) {
      return 'date'; // Default to date if no data
    }

    // Collect sample x values from ALL sensors (not just first one)
    const xSamples: any[] = [];
    for (const sensor of data.data) {
      if (sensor?.x && Array.isArray(sensor.x) && sensor.x.length > 0) {
        // Take first few x values from each sensor
        xSamples.push(...sensor.x.slice(0, 3));
        if (xSamples.length >= 10) break; // Enough samples
      }
    }

    if (xSamples.length === 0) {
      return 'date'; // Default if no x values found
    }

    // Check the types of x values
    let hasNumber = false;
    let hasDateString = false;
    let hasCategoryString = false;

    for (const x of xSamples) {
      if (typeof x === 'number') {
        hasNumber = true;
      } else if (typeof x === 'string') {
        // Try to parse as date
        const parsedDate = new Date(x);
        if (!isNaN(parsedDate.getTime()) && x.match(/\d{4}-\d{2}-\d{2}/)) {
          // Valid ISO date string
          hasDateString = true;
        } else {
          // Regular string (category)
          hasCategoryString = true;
        }
      }
    }

    // Decision logic:
    // 1. If we have numbers, it's numeric (e.g., Hz for spectrum)
    if (hasNumber) {
      return 'numeric';
    }

    // 2. If we have date strings, it's date (e.g., time series)
    if (hasDateString) {
      return 'date';
    }

    // 3. If we have category strings, it's category
    if (hasCategoryString) {
      return 'category';
    }

    // Fallback to date
    return 'date';
  }, [data]);

  // Check if numeric x-values exceed 1000 (for k-notation formatting)
  const xAxisKNotationConfig = React.useMemo(() => {
    if (xAxisType !== 'numeric') return null;

    // Check all x-values across all sensors
    const allXValues: number[] = [];
    data?.data?.forEach((sensor: any) => {
      if (Array.isArray(sensor.x)) {
        sensor.x.forEach((x: any) => {
          if (typeof x === 'number') {
            allXValues.push(x);
          }
        });
      }
    });

    if (allXValues.length === 0) return null;

    // Use k-notation if any value is >= 1000
    const shouldUseK = allXValues.some((val) => Math.abs(val) >= 1000);
    if (!shouldUseK) return null;

    // Find min and max for determining tick values (use loop to avoid stack overflow on large arrays)
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < allXValues.length; i++) {
      const val = allXValues[i];
      if (val < minX) minX = val;
      if (val > maxX) maxX = val;
    }

    // Generate reasonable tick values
    const range = maxX - minX;
    const numTicks = 8; // Approximate number of ticks
    const roughStep = range / numTicks;

    // Round step to nice number (in thousands)
    const stepInK = Math.max(1, Math.ceil(roughStep / 1000));
    const step = stepInK * 1000;

    // Generate tick values - stay within data range
    const tickvals: number[] = [];
    const ticktext: string[] = [];

    const startTick = Math.floor(minX / step) * step;
    const endTick = Math.ceil(maxX / step) * step;

    for (let tick = startTick; tick <= endTick; tick += step) {
      // Only include ticks within or near the data range
      if (tick >= minX * 0.95 && tick <= maxX * 1.05) {
        tickvals.push(tick);
        const kValue = tick / 1000;
        // Format: if it's a whole number, show without decimals; otherwise show 1 decimal
        const formatted = kValue % 1 === 0 ? `${kValue}k` : `${kValue.toFixed(1)}k`;
        ticktext.push(formatted);
      }
    }

    return { tickvals, ticktext, minX, maxX };
  }, [data, xAxisType]);

  // dynamic tickformat selection
  const getYAxisTickFormat = (yValues: Iterable<number>): string => {
    let min = Infinity;
    let max = -Infinity;
    let seen = false;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    for (const v of yValues) {
      if (Number.isFinite(v)) {
        seen = true;
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }

    if (!seen) return '~s';
    if (min < 1 && max < 10) return '.1f';
    return '~s';
  };

  // Add ref to track if we're in the middle of a programmatic update
  const isUpdatingRef = useRef(false);
  // Add ref to track if we're in a reset operation
  const isResettingRef = useRef(false);

  // Track data changes to force re-render ***
  const [dataVersion, setDataVersion] = useState(0);
  const dataRef = useRef(data);

  // Determine if this is a split chart (individual sensor chart) ***
  const isSplitChart = labels.length === 1 && sensorName;

  // Handle preserved range application after chart loads ***
  useEffect(() => {
    if (preservedTimeRange && isModeTransition && isLoaded && !hasAppliedPreservedRangeRef.current) {
      // Clear any existing timeout
      if (rangeApplicationTimeoutRef.current) {
        clearTimeout(rangeApplicationTimeoutRef.current);
      }

      // Apply the preserved range with a delay to ensure chart is ready
      rangeApplicationTimeoutRef.current = setTimeout(() => {
        setInternalTimeRange(preservedTimeRange);
        hasAppliedPreservedRangeRef.current = true;

        // Use Plotly.relayout to force the range
        const plotElement = plotRef.current?.el;
        if (plotElement && (window as any).Plotly) {
          const rangeUpdate = {
            'xaxis.range': [preservedTimeRange[0].toISOString(), preservedTimeRange[1].toISOString()],
            'xaxis.autorange': false,
          };

          (window as any).Plotly.relayout(plotElement, rangeUpdate)
            .then(() => {})
            .catch((error: any) => {});
        }
      }, 100); // Short delay to ensure chart is rendered
    }
  }, [preservedTimeRange, isModeTransition, isLoaded]);

  // *** RESET application flag when mode transition ends ***
  useEffect(() => {
    if (!isModeTransition) {
      hasAppliedPreservedRangeRef.current = false;
      setInternalTimeRange(null);
    }
  }, [isModeTransition]);

  // *** REQUEST CANCELLATION: Detect changes that should cancel requests ***
  useEffect(() => {
    // Create a lightweight key for data - avoid JSON.stringify on huge arrays (waveform data has 200k+ samples)
    let currentDataKey: string;
    try {
      // For large datasets, use a hash based on structure rather than full serialization
      if (data?.data && Array.isArray(data.data)) {
        const dataInfo = data.data.map((d: any) => ({
          name: d.displayName || d.name,
          xLen: d.x?.length || d.samples?.length || 0,
          yLen: d.y?.length || 0,
        }));
        currentDataKey = JSON.stringify(dataInfo);
      } else {
        currentDataKey = JSON.stringify(data);
      }
    } catch {
      // Fallback if data is too large - use timestamp
      currentDataKey = `data-${Date.now()}`;
    }
    const currentEnabledSensorsKey = JSON.stringify(enabledSensors);

    const dataChanged = currentDataKey !== previousDataRef.current;
    const sensorsChanged = currentEnabledSensorsKey !== previousEnabledSensorsRef.current;

    if (dataChanged || sensorsChanged) {
      // *** GENERATE NEW COMPONENT ID TO INVALIDATE PREVIOUS REQUESTS ***
      const oldComponentId = chartComponentIdRef.current;
      chartComponentIdRef.current = Math.random().toString(36).substr(2, 9);

      previousDataRef.current = currentDataKey;
      previousEnabledSensorsRef.current = currentEnabledSensorsKey;

      // Update data version to trigger re-render
      setDataVersion((prev) => prev + 1);
    }
  }, [data, enabledSensors, chartId]);

  // ***  Force chart update when data changes ***
  useEffect(() => {
    // Use lightweight comparison to avoid JSON.stringify on huge arrays (waveform data has 200k+ samples)
    const getDataFingerprint = (d: any): string => {
      try {
        if (d?.data && Array.isArray(d.data)) {
          return JSON.stringify(
            d.data.map((item: any) => ({
              name: item.displayName || item.name,
              xLen: item.x?.length || item.samples?.length || 0,
              yLen: item.y?.length || 0,
            }))
          );
        }
        return JSON.stringify(d);
      } catch {
        return `data-${Date.now()}`;
      }
    };

    const currentDataString = getDataFingerprint(data);
    const prevDataString = getDataFingerprint(dataRef.current);

    if (currentDataString !== prevDataString) {
      dataRef.current = data;
      setDataVersion((prev) => prev + 1);
    }
  }, [data]);

  // Early validation - check if data exists before accessing properties
  const anySensorsEnabled = enabledSensors ? Object.values(enabledSensors).some((value) => value === true) : false;

  useEffect(() => {
    renderStartTime.current = performance.now();
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (isLoaded) {
      const endTime = performance.now();
      setRenderTime(endTime - renderStartTime.current);
    }
  }, [isLoaded]);

  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (rangeApplicationTimeoutRef.current) {
        clearTimeout(rangeApplicationTimeoutRef.current);
      }
    };
  }, []);

  // Handle window resize events to update chart dimensions
  useEffect(() => {
    const handleResize = () => {
      if (plotRef.current) {
        // Trigger Plotly's internal resize handler
        const plotElement = plotRef.current.el;
        if (plotElement && window.Plotly) {
          window.Plotly.Plots.resize(plotElement);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Additional validation for required data properties
  const validateData = () => {
    // First check if data exists
    if (!data) {
      return false;
    }

    if (!data.data || !Array.isArray(data.data)) {
      return false;
    }

    // Check if this is alarm data (bar chart)
    const isAlarmChart = data.data.some((s: any) => s.type === 'bar');
    if (isAlarmChart) {
    }

    if (!enabledSensors || typeof enabledSensors !== 'object') {
      return false;
    }

    // Check if at least one enabled sensor has valid numeric data
    let hasValidSensor = false;

    // Get all enabled sensor labels
    const enabledLabels = Object.keys(enabledSensors).filter((key) => enabledSensors[key]);

    // If no sensors are enabled, return false
    if (enabledLabels.length === 0) {
      return false;
    }

    // Try to validate through labels array if available
    if (labels && Array.isArray(labels) && labels.length > 0) {
      for (let i = 0; i < labels.length; i++) {
        const label = labels[i];

        // Skip if this sensor is not enabled
        if (!enabledSensors[label]) continue;

        const sensor = data.data.find((s: any) => s.displayName === label || s.name === label);

        // Check if sensor is enabled, has arrays, and arrays have matching length
        if (sensor?.x?.length && sensor?.y?.length === sensor?.x?.length) {
          // CRITICAL: Also verify that at least one y value is a valid number
          // This prevents false positives when arrays exist but contain only NaN/null values
          const hasValidNumbers = sensor.y.some((val: any) => typeof val === 'number' && !isNaN(val) && isFinite(val));

          if (hasValidNumbers) {
            hasValidSensor = true;
            break;
          }
        }
      }
    }

    // If no valid sensor found through labels (or labels is empty), check all data sensors directly
    if (!hasValidSensor) {
      for (const sensor of data.data) {
        const sensorLabel = sensor.displayName || sensor.name;
        if (enabledSensors[sensorLabel] && sensor?.x?.length && sensor?.y?.length === sensor?.x?.length) {
          const hasValidNumbers = sensor.y.some((val: any) => typeof val === 'number' && !isNaN(val) && isFinite(val));

          if (hasValidNumbers) {
            hasValidSensor = true;
            break;
          }
        }
      }
    }

    if (!hasValidSensor) {
      return false;
    }

    return true;
  };

  const start = new Date(data?.start_datetime || '');
  const end = new Date(data?.end_datetime || '');

  // Validate that the dates are valid
  const isValidDate = (date: Date) => !isNaN(date.getTime());

  // For numeric x-axis (spectrum/frequency data), don't require valid dates
  // For date x-axis, require valid start/end dates
  // For waveform, allow epoch-based dates (x values are already Date ISO strings)
  const isValidData =
    data &&
    validateData() &&
    (xAxisType === 'numeric' || isSpectrum || isWaveform || (isValidDate(start) && isValidDate(end)));

  // *** CRITICAL: Get the appropriate time range with proper priority ***
  // Memoized to prevent re-calculation on every render - MUST be declared before any conditional logic
  const timeRange = React.useMemo(() => {
    // Helper function to safely convert dates to ISO strings
    const safeToISO = (date: Date) => {
      if (!date || !isValidDate(date)) {
        return new Date().toISOString(); // Fallback to current date
      }
      return date.toISOString();
    };

    // 1. During mode transitions with internal range applied, use internal range
    if (isModeTransition && internalTimeRange && internalTimeRange[0] && internalTimeRange[1]) {
      return [safeToISO(internalTimeRange[0]), safeToISO(internalTimeRange[1])];
    }

    // 2. During mode transitions with preserved range, use preserved range
    if (isModeTransition && preservedTimeRange && preservedTimeRange[0] && preservedTimeRange[1]) {
      return [safeToISO(preservedTimeRange[0]), safeToISO(preservedTimeRange[1])];
    }

    // 3. Use sharedTimeRange if available
    if (sharedTimeRange && sharedTimeRange[0] && sharedTimeRange[1]) {
      return [safeToISO(sharedTimeRange[0]), safeToISO(sharedTimeRange[1])];
    }

    // 4. Default to data range
    return [safeToISO(start), safeToISO(end)];
  }, [isModeTransition, internalTimeRange, preservedTimeRange, sharedTimeRange, start, end]);

  // *** UPDATED: Calculate Y range - individual for split charts, or use globalYRange for combined ***
  const calculateYRange = React.useCallback(() => {
    if (!isValidData) return null;

    let newMinY = Number.POSITIVE_INFINITY;
    let newMaxY = Number.NEGATIVE_INFINITY;

    const relevantThresholdValues = (showThresholds ? thresholds || [] : [])
      .filter((t) => {
        if (!t.visible) return false;
        if (sensorName && t.sensorName !== sensorName) return false;
        return true;
      })
      .map((t) => t.value);

    const expandRange = (vals: number[]) => {
      if (vals.length === 0) return;
      // Use loop instead of spread to avoid stack overflow on large arrays (waveform has 200k+ samples)
      let min = Number.POSITIVE_INFINITY;
      let max = Number.NEGATIVE_INFINITY;
      for (let i = 0; i < vals.length; i++) {
        const val = vals[i];
        if (val < min) min = val;
        if (val > max) max = val;
      }
      if (isFinite(min) && isFinite(max)) {
        newMinY = Math.min(newMinY, min);
        newMaxY = Math.max(newMaxY, max);
      }
    };

    if (isSplitChart) {
      const sensor = data.data?.find((s: any) => s.displayName === sensorName);
      if (sensor && Array.isArray(sensor.y)) {
        const validData = sensor.y.filter((val: any) => typeof val === 'number' && isFinite(val));
        expandRange(validData);
      }
    } else {
      labels.forEach((label) => {
        if (!enabledSensors[label]) return;
        const sensor = data.data?.find((s: any) => s.displayName === label);
        if (sensor && Array.isArray(sensor.y)) {
          const validData = sensor.y.filter((val: any) => typeof val === 'number' && isFinite(val));
          expandRange(validData);
        }
      });
    }

    // Add thresholds
    expandRange(relevantThresholdValues);

    if (newMinY !== Number.POSITIVE_INFINITY && newMaxY !== Number.NEGATIVE_INFINITY) {
      const padding = Math.max((newMaxY - newMinY) * 0.1, 1);
      return [newMinY - padding, newMaxY + padding] as [number, number];
    }

    return null;
  }, [isValidData, showThresholds, thresholds, sensorName, isSplitChart, data, labels, enabledSensors]);

  const [yRange, setYRange] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!isValidData) return;

    const newYRange = calculateYRange();
    if (newYRange) {
      setYRange(newYRange);
    }
  }, [calculateYRange, isValidData]);

  useEffect(() => {
    if (yRange) {
      setOriginalYRange(yRange);
      setMinY(yRange[0]);
      setMaxY(yRange[1]);
    }
  }, [yRange]);

  // Handle hover events for custom popover
  const handleHover = React.useCallback(
    (eventData: any) => {
      if (!eventData || !eventData.points || eventData.points.length === 0) {
        setPopoverData((prev) => ({ ...prev, isVisible: false }));
        // Clear hover position when not hovering over data
        if (isPendingMode) {
          setHoverXPosition(null);
        }
        return;
      }

      try {
        const point = eventData.points[0];
        let timestamp = point.x;
        // const hoveredSeriesName = point.data.name; // Not currently used - we iterate through all series below

        // Track hover position when in pending mode (for purple line indicator)
        if (isPendingMode) {
          // Extract x value (could be date string or number)
          const xValue = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
          setHoverXPosition(xValue);
        }

        // Ensure that timestamp has time
        const noTime = /^\d{4}-\d{2}-\d{2}$/.test(timestamp);
        if (noTime) timestamp = `${timestamp} 00:00`;

        // Check if we're in spectrum focus mode with multiple timestamps
        const hasExpandableLegend = expandableLegendSeries && expandableLegendSeries.length > 0;
        const hasChipValues = isSpectrum && data.data.some((s: any) => s.chip_value);
        const isSpectrumFocusMode = hasExpandableLegend && hasChipValues;

        // Get mouse position from the event
        const mouseX = eventData.event?.clientX || 0;
        const mouseY = eventData.event?.clientY || 0;

        let values: any[] = [];

        if (isSpectrumFocusMode) {
          // SPECTRUM FOCUS MODE: Show subrows (timestamps) instead of sensors
          // Collect values from all enabled timestamps (subrows)
          expandableLegendSeries.forEach((series: any) => {
            if (!series.isFocused || !enabledSensors[series.label]) return;

            const sensorBaseColor = series.color || '#6667dd';

            series.timestamps
              .filter((t: any) => t.enabled)
              .forEach((timestampItem: any) => {
                // Find the data entry with matching chip_value
                // Special handling for Latest/Closest variations
                const isLatestTimestamp =
                  timestampItem.id === '__latest__' ||
                  timestampItem.id === 'latest' ||
                  timestampItem.id === '0' ||
                  timestampItem.label === 'Latest' ||
                  timestampItem.timestamp === 'Closest' ||
                  timestampItem.timestamp === '__latest__' ||
                  timestampItem.timestamp === 'latest';

                let sensorData;
                if (isLatestTimestamp) {
                  // Match Latest timestamp to any chip_value that indicates "latest"
                  sensorData = data.data.find(
                    (s: any) =>
                      s.displayName === series.label &&
                      (s.chip_value === 'Closest' ||
                        s.chip_value === '__latest__' ||
                        s.chip_value === 'latest' ||
                        s.chip_value === '0')
                  );
                } else {
                  // Normal timestamp matching
                  sensorData = data.data.find(
                    (s: any) => s.displayName === series.label && s.chip_value === timestampItem.timestamp
                  );
                }

                if (!sensorData) return;

                // Find value at the hovered x position
                const sensorTimestampIndex = sensorData.x.findIndex(
                  (ts: string) => new Date(ts).getTime() === new Date(timestamp).getTime()
                );

                let value = 0;
                let tooltipData: Record<string, any> | undefined;

                if (sensorTimestampIndex !== -1) {
                  value = sensorData.y[sensorTimestampIndex] || 0;

                  // Extract tooltip data if available
                  if (sensorData.tooltip) {
                    if (Array.isArray(sensorData.tooltip)) {
                      tooltipData = sensorData.tooltip[sensorTimestampIndex];
                    } else if (typeof sensorData.tooltip === 'object') {
                      tooltipData = sensorData.tooltip[series.label] || sensorData.tooltip[timestamp];
                    }
                  }

                  if (!tooltipData && sensorData.alarmTooltips) {
                    if (Array.isArray(sensorData.alarmTooltips)) {
                      tooltipData = sensorData.alarmTooltips[sensorTimestampIndex];
                    }
                  }

                  if (!tooltipData && sensorData.tooltipData) {
                    if (Array.isArray(sensorData.tooltipData)) {
                      tooltipData = sensorData.tooltipData[sensorTimestampIndex];
                    } else if (typeof sensorData.tooltipData === 'object') {
                      tooltipData = sensorData.tooltipData[series.label] || sensorData.tooltipData[timestamp];
                    }
                  }
                }

                values.push({
                  name: timestampItem.label, // Use timestamp label (e.g., "T1 - Sep 20, 20:00")
                  value: typeof value === 'number' ? value : 0,
                  color: timestampItem.color || sensorBaseColor,
                  unit: unit || '',
                  ...(tooltipData && { tooltipData }),
                });
              });
          });

          // Filter out zero values if showZeroInTooltip is false
          values = values.filter((item) => showZeroInTooltip || item.value !== 0);
        } else {
          // NORMAL MODE: Collect values for all enabled sensors at this timestamp
          values = labels
            .filter((label) => enabledSensors[label])
            .map((label) => {
              const sensorData = data.data.find((s: any) => s.displayName === label);

              // Find the correct timestamp index for THIS sensor
              let value = 0;
              let tooltipData: Record<string, any> | undefined;

              if (sensorData && sensorData.x && sensorData.y) {
                const sensorTimestampIndex = sensorData.x.findIndex(
                  (ts: string) => new Date(ts).getTime() === new Date(timestamp).getTime()
                );
                if (sensorTimestampIndex !== -1) {
                  value = sensorData.y[sensorTimestampIndex] || 0;

                  // Extract tooltip data if available

                  if (sensorData.tooltip) {
                    // Handle array of tooltip data (indexed by timestamp)
                    if (Array.isArray(sensorData.tooltip)) {
                      tooltipData = sensorData.tooltip[sensorTimestampIndex];
                    }
                    // Handle object with sensor-specific tooltip data
                    else if (typeof sensorData.tooltip === 'object') {
                      // Check if tooltip data exists for this specific sensor
                      if (sensorData.tooltip[label]) {
                        tooltipData = sensorData.tooltip[label];
                      }
                      // Or check if there's tooltip data for this timestamp
                      else if (sensorData.tooltip[timestamp]) {
                        tooltipData = sensorData.tooltip[timestamp];
                      }
                    }
                  }

                  // Also check for alarmTooltips (for transformed alarm data)
                  if (!tooltipData && sensorData.alarmTooltips) {
                    if (Array.isArray(sensorData.alarmTooltips)) {
                      tooltipData = sensorData.alarmTooltips[sensorTimestampIndex];
                    }
                  }

                  // Also check for tooltipData directly on sensorData
                  if (!tooltipData && sensorData.tooltipData) {
                    if (Array.isArray(sensorData.tooltipData)) {
                      tooltipData = sensorData.tooltipData[sensorTimestampIndex];
                    } else if (typeof sensorData.tooltipData === 'object') {
                      tooltipData = sensorData.tooltipData[label] || sensorData.tooltipData[timestamp];
                    }
                  }
                }
              }

              return {
                name: label,
                value: typeof value === 'number' ? value : 0,
                color: colors[labels.indexOf(label)] || '#000000',
                unit: unit || '',
                ...(tooltipData && { tooltipData }),
              };
            })
            // Filter out zero values if showZeroInTooltip is false
            .filter((item) => showZeroInTooltip || item.value !== 0);
        }

        setPopoverData({
          isVisible: true,
          position: { x: mouseX, y: mouseY },
          data: {
            timestamp: timestamp,
            values: values,
          },
        });
      } catch (error) {
        setPopoverData((prev) => ({ ...prev, isVisible: false }));
      }

      // Check if hovering over custom line annotation
      try {
        if (eventData.event && eventData.event.target) {
          const target = eventData.event.target;
          const targetText = target.textContent || '';

          // Check if hovering over any custom line icon
          if (targetText && (targetText.includes('🦄') || targetText.includes('🧸'))) {
            const mouseX = eventData.event.clientX;
            const mouseY = eventData.event.clientY;

            // Get the hovered point's x value to find the correct line
            const hoveredX = eventData.points?.[0]?.x;

            // Find the custom line at this x position with matching icon
            const customLine = allVerticalLines.find((line) => {
              if (!line.isCustomLine || !line.groupIcon) return false;

              // Check if icon matches
              if (!targetText.includes(line.groupIcon)) return false;

              // For better matching, check if timestamps are close
              if (hoveredX) {
                const lineTime = new Date(line.timestamp).getTime();
                const hoverTime = new Date(hoveredX).getTime();
                const timeDiff = Math.abs(lineTime - hoverTime);
                // Within 1 day tolerance
                return timeDiff < 86400000;
              }

              return true;
            });

            if (customLine && !disableCustomLineHoverTooltip) {
              // Show tooltip on hover (only if not disabled)
              setCustomLineTooltip({
                visible: true,
                x: mouseX,
                y: mouseY - 50, // Position above cursor
                lineData: {
                  label: customLine.displayLabel || customLine.label,
                  title: `${customLine.groupIcon} ${customLine._systemGroupLabel || 'Event'}`,
                  description: customLine.description || '',
                  username: customLine.username,
                  source: customLine.source || 'user',
                  groupId: customLine.groupId,
                  dateAdded: customLine.dateAdded
                    ? formatCustomDate(new Date(customLine.dateAdded))
                    : customLine.createdAt
                    ? formatCustomDate(new Date(customLine.createdAt))
                    : '',
                  editedAt: customLine.editedAt ? formatCustomDate(new Date(customLine.editedAt)) : '',
                  editedBy: customLine.editedBy || '',
                  mediaType: customLine.mediaType || ('image' as const),
                  mediaUrl: customLine.mediaUrl || null,
                  thumbnailUrl: customLine.thumbnailUrl || null,
                  capturedData: customLine.capturedData || null,
                  severity: customLine.severity || 'info',
                },
              });
            }
          } else {
            // Hide tooltip when not hovering over custom icon
            if (customLineTooltip.visible) {
              setCustomLineTooltip({ visible: false, x: 0, y: 0, lineData: null });
            }
          }
        }
      } catch (iconError) {
        console.error('Icon hover error:', iconError);
      }
    },
    [
      data,
      labels,
      enabledSensors,
      colors,
      unit,
      showZeroInTooltip,
      isPendingMode,
      expandableLegendSeries,
      isSpectrum,
      allVerticalLines,
      chartId,
      customLineTooltip,
      disableCustomLineHoverTooltip,
    ]
  );

  const handleUnhover = React.useCallback(() => {
    setPopoverData((prev) => ({ ...prev, isVisible: false }));
    // Also hide custom line tooltip when unhovering
    setCustomLineTooltip({ visible: false, x: 0, y: 0, lineData: null });
  }, []);

  // Handle selection events with improved error handling
  const handleSelection = (event: any) => {
    if (!selectionMode) return;

    try {
      if (event && event.range && event.range.x && event.range.x.length >= 2) {
        const startTime = new Date(event.range.x[0]);
        const endTime = new Date(event.range.x[1]);
        onAddSelectedRange(startTime, endTime, chartId);
      } else if (event && event.points && event.points.length >= 2) {
        const times = event.points.map((p: any) => new Date(p.x)).sort((a: Date, b: Date) => a.getTime() - b.getTime());
        const startTime = times[0];
        const endTime = times[times.length - 1];
        onAddSelectedRange(startTime, endTime, chartId);
      }
    } catch (error) {}
  };

  // Handle clicks on annotations (harmonic, vertical lines, and map pins)
  const handleAnnotationClick = React.useCallback(
    (event: any) => {
      if (!event || !event.annotation || !event.annotation.name) {
        return;
      }

      const annotationName = event.annotation.name;

      // Get mouse position from the event
      const mouseX = event.event?.clientX || event.clientX || 0;
      const mouseY = event.event?.clientY || event.clientY || 0;

      // Check if this is a harmonic starting point annotation
      if (annotationName.startsWith('harmonic-')) {
        const lineId = annotationName.replace('harmonic-', '');

        if (onHarmonicLineClick) {
          onHarmonicLineClick(lineId, { x: mouseX, y: mouseY });
        }
      }
      // Check if this is a map pin (user marker) annotation
      else if (annotationName.startsWith('map-pin-')) {
        const lineId = annotationName.replace('map-pin-', '');

        if (onVerticalLineClick) {
          onVerticalLineClick(lineId, { x: mouseX, y: mouseY }, chartId);
        }
      }
      // Check if this is a vertical line (system marker) annotation
      else if (annotationName.startsWith('vertical-line-')) {
        const lineId = annotationName.replace('vertical-line-', '');

        if (onVerticalLineClick) {
          onVerticalLineClick(lineId, { x: mouseX, y: mouseY }, chartId);
        }
      }
      // Check if this is a custom line annotation
      else if (annotationName.startsWith('custom-line-')) {
        const lineId = annotationName.replace('custom-line-', '');
        const currentTime = Date.now();

        // Check for double-click (within 500ms of last click on same line)
        const isDoubleClick = lineId === customLastClickedLineId && currentTime - lastClickTime.current < 500;

        // Get custom data from the event
        const customData = event.annotation?.customdata;

        // Find the custom line data
        const customLine = allVerticalLines.find((line) => line.id === lineId && line.isCustomLine);
        if (customLine) {
          if (isDoubleClick) {
            // Double-click: Trigger the handler
            // Call the appropriate handler based on what's provided
            if (onVerticalLineClick) {
              // Use the vertical line click handler (preferred for custom lines)
              onVerticalLineClick(lineId, { x: mouseX, y: mouseY }, chartId);
            } else if (onCustomLineDoubleClick) {
              // Fallback to custom double-click handler if provided
              onCustomLineDoubleClick(customLine, { x: mouseX, y: mouseY });
            } else {
              // Fallback to alert if no handler provided
              const alertMessage = `
Custom Line Details:
━━━━━━━━━━━━━━━━━━━
Icon: ${customLine.groupIcon || '📍'}
Label: ${customLine.displayLabel || customLine.label || 'Custom Event'}
Group: ${customLine._systemGroupLabel || 'Unknown Group'}
Added by: ${customLine.username || 'System'}
Timestamp: ${customLine.timestamp ? formatCustomDate(new Date(customLine.timestamp)) : 'Unknown'}
Source: ${customLine.source || 'Unknown'}
Chart ID: ${customLine.chartId || 'All Charts'}
━━━━━━━━━━━━━━━━━━━
              `.trim();

              alert(alertMessage);
            }

            // Close menu if open
            setCustomLineMenu(null);

            // Reset the click tracking to prevent triple-click issues
            setCustomLastClickedLineId(null);
            lastClickTime.current = 0;
          } else if (!disableCustomLinePopoverMenu) {
            // Single click: Show menu (only if not disabled)
            setCustomLineMenu({
              isOpen: true,
              lineData: {
                id: customLine.id,
                label: customLine.displayLabel || customLine.label,
                groupId: customLine.groupId,
                source: customLine.source || (customLine.isSystemLine ? 'system' : 'user'),
                username: customLine.username,
                timestamp: customLine.timestamp,
                dimmed: customLine.dimmed, // Include dimmed state for Dim/Undim button
              },
              position: { x: mouseX, y: mouseY },
              chartId,
            });

            // Update click tracking for double-click detection
            lastClickTime.current = currentTime;
            setCustomLastClickedLineId(lineId);
          }
        }
      }
    },
    [
      onHarmonicLineClick,
      onVerticalLineClick,
      allVerticalLines,
      chartId,
      customLastClickedLineId,
      disableCustomLinePopoverMenu,
      onCustomLineDoubleClick,
    ]
  );

  const handleClick = React.useCallback(
    (event: any) => {
      const currentTime = Date.now();
      const timeSinceLastClick = currentTime - lastClickTime.current;

      clickCountRef.current += 1;
      const clickId = `click-${currentTime}-${clickCountRef.current}`;

      // Check if clicking on a custom line icon by looking at the event
      if (event?.points?.[0]?.data?.name?.startsWith('custom-line-')) {
        // Trigger the annotation click handler manually
        handleAnnotationClick({
          annotation: {
            name: event.points[0].data.name,
            customdata: event.points[0].customdata,
          },
          event: event.event,
        });
        return;
      }

      // Handle waveform seek - clicking on chart seeks to that position
      if (isWaveform && onWaveformSeek && event?.points?.[0]?.x) {
        const clickedX = event.points[0].x;
        // Convert epoch-based date string to seconds
        const clickedDate = new Date(clickedX);
        const seekTimeSeconds = clickedDate.getTime() / 1000;
        onWaveformSeek(seekTimeSeconds);
        return;
      }

      // Handle custom line mode clicks FIRST
      if (isCustomLineMode || activeCustomLineMode) {
        // Use new dynamic system if active, otherwise fall back to old system
        const currentMode = activeCustomLineMode || isCustomLineMode;

        if (activeCustomLineMode && customLineGroups[activeCustomLineMode]) {
          // Handle dynamic custom line mode click
          if (!event || !event.points || event.points.length === 0) {
            return;
          }

          const clickedXValue = event.points[0].x;
          const mouseX = event.event?.clientX || 0;
          const mouseY = event.event?.clientY || 0;

          // Get current user info from auth context
          const { user } = useAuth();
          const username = user?.email || 'Unknown User';

          // Extract Y values from ALL ACTIVE/ENABLED sensors at this timestamp
          const yValues: { [key: string]: number } = {};

          // Get all ACTIVE sensor values at this timestamp
          if (data?.data && Array.isArray(data.data)) {
            data.data.forEach((trace: any) => {
              // Only include traces that are currently enabled/visible
              if (trace.x && trace.y && trace.name && trace.visible !== false) {
                // Check if this sensor is enabled in the legend
                const sensorEnabled = enabledSensors[trace.name] !== false;

                if (sensorEnabled) {
                  // Find the index for this timestamp with better matching
                  let timeIndex = -1;
                  let bestTimeDiff = Infinity;

                  trace.x.forEach((t: any, i: number) => {
                    const traceTime = typeof t === 'string' ? new Date(t) : new Date(t);
                    const clickTime =
                      typeof clickedXValue === 'string' ? new Date(clickedXValue) : new Date(clickedXValue);
                    const timeDiff = Math.abs(traceTime.getTime() - clickTime.getTime());

                    if (timeDiff < bestTimeDiff) {
                      bestTimeDiff = timeDiff;
                      timeIndex = i;
                    }
                  });

                  if (timeIndex !== -1 && trace.y[timeIndex] !== undefined && bestTimeDiff < 5000) {
                    // Within 5 seconds
                    const value = Number(trace.y[timeIndex].toFixed(3));
                    yValues[trace.name] = value;
                  }
                }
              }
            });
          }

          // Fallback: Use clicked point if no other data found
          if (
            Object.keys(yValues).length === 0 &&
            event.points &&
            event.points[0] &&
            typeof event.points[0].y === 'number'
          ) {
            yValues[event.points[0].data.name || 'value'] = event.points[0].y;
          }

          const lineData = {
            timestamp: typeof clickedXValue === 'string' ? new Date(clickedXValue) : new Date(clickedXValue),
            values: { ...yValues },
            clickedXValue,
            label: `New ${customLineGroups[activeCustomLineMode].groupLabel}`,
            username,
          };

          // Add to local custom lines state
          setCustomVerticalLinesState((prevState: any) => {
            const newState = { ...prevState };
            if (!newState.groups) newState.groups = [];

            // Find or create the group
            let groupIndex = newState.groups.findIndex((g: any) => g.groupId === activeCustomLineMode);
            if (groupIndex === -1) {
              newState.groups.push({
                groupId: activeCustomLineMode,
                groupLabel: customLineGroups[activeCustomLineMode]?.groupLabel,
                groupIcon: customLineGroups[activeCustomLineMode]?.groupIcon,
                groupColor: customLineGroups[activeCustomLineMode]?.groupColor,
                visible: true,
                groupItems: [],
              });
              groupIndex = newState.groups.length - 1;
            }

            // Add the new line item
            const newItem = {
              id: `custom-${Date.now()}`,
              x: lineData.timestamp, // Use x for position, not value
              value: username, // The value field contains the actual username
              label: lineData.label,
              source: 'user',
              username: username, // Use username instead of addedBy for consistency
              addedBy: username, // Keep for backward compatibility
              chartId: syncCustomLines ? undefined : chartId,
              groupIcon: customLineGroups[activeCustomLineMode]?.groupIcon, // Add the icon
              dateAdded: new Date().toISOString(), // When this line was added by the user
              createdAt: new Date().toISOString(), // Alternative field name
              editedAt: null, // When line was last edited
              editedBy: null, // Who last edited the line
              severity: 'info', // Default severity for new lines
              // Capture the actual data values at this point
              capturedData: {
                xValue: lineData.clickedXValue || lineData.timestamp,
                yValues: lineData.values || {},
                timestamp: lineData.timestamp,
              },
            };

            newState.groups[groupIndex].groupItems = [...(newState.groups[groupIndex].groupItems || []), newItem];

            return newState;
          });
          return;
        }

        // Fall back to existing system
        if (handleCustomLineClick) {
          const handled = handleCustomLineClick(event);
          if (handled) {
            return; // Custom line click was handled, stop here
          }
        }
      }

      // Skip click handling if in selection mode
      if (selectionMode) {
        // In selection mode, skip click handler
        return;
      }

      // Handle pending harmonic/side bands creation (second click to set interval)
      if (isPendingMode && pendingHarmonicCreation && onCancelPendingHarmonic) {
        try {
          if (!event || !event.points || event.points.length === 0) {
            return;
          }

          const clickedXValue = event.points[0].x;

          // Extract numeric value from clicked point
          let clickedPoint: number;
          if (typeof clickedXValue === 'number') {
            clickedPoint = clickedXValue;
          } else if (typeof clickedXValue === 'string') {
            const parsedDate = new Date(clickedXValue);
            if (!isNaN(parsedDate.getTime())) {
              clickedPoint = parsedDate.getTime();
            } else {
              return;
            }
          } else if (clickedXValue instanceof Date) {
            clickedPoint = clickedXValue.getTime();
          } else {
            return;
          }

          // Calculate interval from center point
          const interval = Math.abs(clickedPoint - pendingHarmonicCreation.centerPoint);

          const centerPoint = pendingHarmonicCreation.centerPoint;
          const isHarmonicMode = pendingHarmonicCreation.type === 'harmonic';

          if (isHarmonicMode) {
            // HARMONIC MODE: Multiplicative spacing (H1 = 2×H0, H2 = 3×H0, H3 = 4×H0, etc.)
            // H0 was already created on first click at position 'centerPoint'
            const defaultMultiplier = 6; // 6 harmonics forward from H0

            // Create harmonic lines: H1, H2, H3, H4, H5, H6
            // Each harmonic is a multiple of the fundamental frequency (centerPoint)
            for (let i = 1; i <= defaultMultiplier; i++) {
              const position = centerPoint * (i + 1); // Multiplicative: H1 = 2×centerPoint, H2 = 3×centerPoint, etc.
              const label = `${pendingHarmonicCreation.labelPrefix}${i}`;
              const isFirstHarmonic = false; // Only H0 is the first harmonic (already created)

              if (onAddVerticalLine) {
                onAddVerticalLine(
                  position as any,
                  {},
                  position,
                  label,
                  pendingHarmonicCreation.color,
                  true,
                  isFirstHarmonic,
                  pendingHarmonicCreation.chartId,
                  pendingHarmonicCreation.sectionKey,
                  'system',
                  pendingHarmonicCreation.labelPrefix,
                  pendingHarmonicCreation.groupId
                );
              }
            }
          } else {
            // CARRIER MODE: Create lines on both sides of center (-H3 to H3, excluding H0)
            const defaultMultiplier = 3; // 3 lines on each side
            const totalLines = 2 * defaultMultiplier;

            // Create lines from -multiplier to +multiplier, SKIPPING i=0 (H0 already exists)
            for (let i = -defaultMultiplier; i <= defaultMultiplier; i++) {
              if (i === 0) {
                continue;
              }

              const position = centerPoint + i * interval;
              const label =
                i < 0
                  ? `-${pendingHarmonicCreation.labelPrefix}${Math.abs(i)}`
                  : `${pendingHarmonicCreation.labelPrefix}${i}`;
              const isFirstHarmonic = false;

              if (onAddVerticalLine) {
                onAddVerticalLine(
                  position as any,
                  {},
                  position,
                  label,
                  pendingHarmonicCreation.color,
                  true,
                  isFirstHarmonic,
                  pendingHarmonicCreation.chartId,
                  pendingHarmonicCreation.sectionKey,
                  'system',
                  pendingHarmonicCreation.labelPrefix,
                  pendingHarmonicCreation.groupId
                );
              }
            }
          }

          // Clear pending state
          onCancelPendingHarmonic();

          return;
        } catch (error) {
          // Clear pending state on error
          onCancelPendingHarmonic();
          return;
        }
      }

      // Handle side bands selection mode - use two-step workflow
      if (isSideBandsSelectionMode && onGenerateSideBandsFromClick) {
        try {
          if (!event || !event.points || event.points.length === 0) {
            return;
          }

          const clickedXValue = event.points[0].x;

          // Handle both numeric (Hz) and date values
          let startingPoint: number;
          let xAxisType: 'date' | 'numeric' | 'category' = 'numeric';

          if (typeof clickedXValue === 'number') {
            startingPoint = clickedXValue;
            xAxisType = 'numeric';
          } else if (typeof clickedXValue === 'string') {
            const parsedDate = new Date(clickedXValue);
            if (!isNaN(parsedDate.getTime())) {
              startingPoint = parsedDate.getTime();
              xAxisType = 'date';
            } else {
              return;
            }
          } else if (clickedXValue instanceof Date) {
            startingPoint = clickedXValue.getTime();
            xAxisType = 'date';
          } else {
            return;
          }

          // Call onGenerateSideBandsFromClick to trigger pending mode setup
          // The container will handle entering pending mode and exiting side bands selection mode
          onGenerateSideBandsFromClick(startingPoint, xAxisType, chartId);

          return;
        } catch (error) {
          return;
        }
      }

      // Handle harmonic selection mode
      if (isHarmonicSelectionMode && onAddHarmonicSelection) {
        try {
          if (!event || !event.points || event.points.length === 0) {
            return;
          }

          const clickedXValue = event.points[0].x;

          // Handle both numeric (Hz) and date values
          let capturedValue: number;
          let detectedXAxisType: 'date' | 'numeric';

          if (typeof clickedXValue === 'number') {
            // Numeric x-axis (frequency charts)
            capturedValue = clickedXValue;
            detectedXAxisType = 'numeric';
          } else if (typeof clickedXValue === 'string') {
            // Date string x-axis (time-series charts)
            const dateValue = new Date(clickedXValue);
            if (isNaN(dateValue.getTime())) {
              return;
            }
            capturedValue = dateValue.getTime(); // Convert to timestamp
            detectedXAxisType = 'date';
          } else {
            return;
          }

          // Create harmonic selection object with chartId and detected axis type
          const harmonicSelection = {
            id: `harmonic-${Date.now()}`,
            hz: capturedValue,
            label: 'H',
            multiplier: 6,
            chartId: chartId,
            xAxisType: detectedXAxisType,
          };

          onAddHarmonicSelection(harmonicSelection);
          return; // Exit after handling harmonic selection
        } catch (error) {
          return;
        }
      }

      // Handle marker selection mode - only add markers when explicitly in marker mode
      if (!isMarkerSelectionMode) {
        // Not in marker mode, skip click
        return;
      }

      if (!isComparisonEnabled) {
        // Comparison not enabled, skip click
        return;
      }

      // Define the click processing function
      const processClick = () => {
        // Process click for vertical lines
        try {
          if (!event || !event.points || event.points.length === 0) {
            // No event points found
            return;
          }
          if (!isValidData) {
            // Invalid data
            return;
          }

          const clickedXValue = event.points[0].x; // Store the raw x value
          const clickedPoint = event.points[0];

          const clickedSensorName = event.points[0].data.name;
          const clickedYValue = event.points[0].y;

          // Try to get the point index directly from the event if available
          const pointIndex =
            event.points[0].pointIndex !== undefined ? event.points[0].pointIndex : event.points[0].pointNumber;

          let clickedTimestamp: Date | null = null;
          let clickedNumericValue: number | null = null;
          let exactIndex = -1;

          // Handle different x-axis types
          if (xAxisType === 'numeric') {
            // Numeric x-axis (e.g., frequency in Hz for spectrum charts)
            clickedNumericValue = typeof clickedXValue === 'number' ? clickedXValue : parseFloat(clickedXValue);

            if (isNaN(clickedNumericValue)) {
              return;
            }

            const xValues = data.data[0].x;

            // Use point index if available
            if (pointIndex !== undefined && pointIndex >= 0 && pointIndex < xValues.length) {
              exactIndex = pointIndex;
            } else {
              // Find closest numeric value
              let closestIndex = 0;
              let minDiff = Math.abs(Number(xValues[0]) - clickedNumericValue);
              for (let i = 1; i < xValues.length; i++) {
                const diff = Math.abs(Number(xValues[i]) - clickedNumericValue);
                if (diff < minDiff) {
                  minDiff = diff;
                  closestIndex = i;
                }
              }
              exactIndex = closestIndex;
            }
          } else {
            // Date x-axis (time-series charts)
            clickedTimestamp = new Date(clickedXValue);

            // Validate the timestamp
            if (!clickedTimestamp || isNaN(clickedTimestamp.getTime())) {
              return;
            }

            const timestamps = data.data[0].x.map((ts: string) => new Date(ts));

            // If we have a point index from the event, use it directly
            if (pointIndex !== undefined && pointIndex >= 0 && pointIndex < timestamps.length) {
              exactIndex = pointIndex;
            } else {
              // Find exact timestamp match
              for (let i = 0; i < timestamps.length; i++) {
                if (timestamps[i].getTime() === clickedTimestamp.getTime()) {
                  exactIndex = i;
                  break;
                }
              }

              // If no exact match, find closest
              if (exactIndex === -1) {
                let closestIndex = 0;
                let minDiff = Math.abs(timestamps[0].getTime() - clickedTimestamp.getTime());
                for (let i = 1; i < timestamps.length; i++) {
                  const diff = Math.abs(timestamps[i].getTime() - clickedTimestamp.getTime());
                  if (diff < minDiff) {
                    minDiff = diff;
                    closestIndex = i;
                  }
                }
                exactIndex = closestIndex;
              }
            }
          }

          // Validate index is within bounds
          const dataLength = data.data[0].x.length;
          if (exactIndex < 0 || exactIndex >= dataLength) {
            return;
          }

          const values: Record<string, number> = {};

          // Safely extract values for all enabled sensors only
          labels.forEach((label) => {
            // Only include values for enabled sensors
            if (!enabledSensors[label]) return;

            try {
              const sensor = data.data.find((s: any) => s.displayName === label);
              if (label === clickedSensorName) {
                values[label] = clickedYValue;
              } else {
                if (sensor && Array.isArray(sensor.y) && exactIndex < sensor.y.length) {
                  const value = sensor.y[exactIndex];
                  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
                    values[label] = value;
                  } else {
                    values[label] = 0; // Default fallback
                  }
                } else {
                  values[label] = 0; // Default fallback
                }
              }
            } catch (error) {
              values[label] = 0; // Default fallback
            }
          });
          // Adding vertical line with values - default to user markers
          if (verticalLinesEnabled && onAddVerticalLine) {
            // Pass the clicked value (timestamp for date axes, numeric value for frequency axes)
            // and values extracted from the correct index
            // Default to 'user' marker type (Map Pin with M0, M1, M2 labels)
            const lineValue = xAxisType === 'numeric' ? clickedNumericValue : clickedTimestamp;

            // Only add line if we have a valid value
            if (lineValue !== null) {
              onAddVerticalLine(
                lineValue,
                values,
                clickedXValue,
                undefined,
                undefined,
                false,
                false,
                undefined, // Don't pass chartId - markers should appear on ALL charts in the data layer
                undefined, // Don't pass sectionKey - markers should be GLOBAL and visible to ALL sections for benchmark sync
                'user'
              );
            } else {
            }
          } else {
            // Not calling onAddVerticalLine - conditions not met
          }
        } catch (error) {}
      };

      // Debouncing logic - only if enableDoubleClickZoomReset is true
      // When double-click zoom is enabled, we need to wait to see if this is a double-click
      if (enableDoubleClickZoomReset) {
        // Clear any pending click timeout
        if (pendingClickTimeout.current) {
          clearTimeout(pendingClickTimeout.current);
          pendingClickTimeout.current = null;
          // Double-click detected, cancel vertical line
          lastClickTime.current = currentTime;
          return; // This is a double-click, don't add line
        }

        // Update last click time
        lastClickTime.current = currentTime;

        // Set a timeout to process this as a single click
        pendingClickTimeout.current = setTimeout(() => {
          pendingClickTimeout.current = null;
          processClick();
        }, 300); // Wait 300ms to see if it's a double-click
      } else {
        // No debouncing - process click immediately
        processClick();
      }
    },
    [
      isComparisonEnabled,
      selectionMode,
      verticalLinesEnabled,
      onAddVerticalLine,
      isValidData,
      data,
      labels,
      enabledSensors,
      isMarkerSelectionMode,
      isHarmonicSelectionMode,
      onAddHarmonicSelection,
      isSideBandsSelectionMode,
      onGenerateSideBandsFromClick,
      enableDoubleClickZoomReset,
      activeCustomLineMode,
      customLineGroups,
      addLineToCustomGroup,
      handleCustomLineClick,
      handleAnnotationClick,
      isWaveform,
      onWaveformSeek,
    ]
  );

  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
      if (pendingClickTimeout.current) {
        clearTimeout(pendingClickTimeout.current);
      }
    };
  }, []);

  // Set up a global mouse move listener to detect hovering over icons
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if we're hovering over a Plotly annotation text
      if (target && target.closest('.annotation-text')) {
        const text = target.textContent || '';

        // Check if it's one of our custom icons
        if (text && (text.includes('🦄') || text.includes('🧸'))) {
          // Find the matching custom line
          const customLine = allVerticalLines.find(
            (line) => line.isCustomLine && line.groupIcon && text.includes(line.groupIcon)
          );

          if (customLine && !customLineTooltip.visible && !disableCustomLineHoverTooltip) {
            // Show tooltip (only if not disabled)
            setCustomLineTooltip({
              visible: true,
              x: e.clientX,
              y: e.clientY - 100,
              lineData: {
                label: customLine.displayLabel || customLine.label || 'Custom Event',
                title: `${customLine.groupIcon} ${customLine._systemGroupLabel || 'Event'}`,
                description: customLine.description || '',
                username: customLine.username, // Use the actual username, no fallback to 'System'
                source: customLine.source || 'user',
                groupId: customLine.groupId || customLine._systemGroupId,
                dateAdded: customLine.dateAdded
                  ? formatCustomDate(new Date(customLine.dateAdded))
                  : customLine.createdAt
                  ? formatCustomDate(new Date(customLine.createdAt))
                  : '',
                editedAt: customLine.editedAt ? formatCustomDate(new Date(customLine.editedAt)) : '',
                editedBy: customLine.editedBy || '',
                mediaType: customLine.mediaType || ('image' as const),
                mediaUrl: customLine.mediaUrl || null,
                thumbnailUrl: customLine.thumbnailUrl || null,
                capturedData: customLine.capturedData || null,
                severity: customLine.severity || 'info',
              },
            });
          }
        } else {
          // Not hovering over an icon, hide tooltip
          if (customLineTooltip.visible) {
            setCustomLineTooltip({ visible: false, x: 0, y: 0, lineData: null });
          }
        }
      } else {
        // Not hovering over any annotation
        if (customLineTooltip.visible) {
          setCustomLineTooltip({ visible: false, x: 0, y: 0, lineData: null });
        }
      }
    };

    // Add global listener
    document.addEventListener('mousemove', handleGlobalMouseMove, { passive: true });

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [allVerticalLines, customLineTooltip.visible, disableCustomLineHoverTooltip]);

  // Filter valid vertical lines once to avoid redundant filtering
  // Also filter by chartId: lines with no chartId show on all charts, lines with chartId only show on that chart
  const validVerticalLines = React.useMemo(
    () =>
      allVerticalLines.filter((line) => {
        // Check if timestamp is valid (Date or number)
        const hasValidTimestamp =
          line.timestamp &&
          (typeof line.timestamp === 'number' ? !isNaN(line.timestamp) : !isNaN(line.timestamp.getTime()));

        // For modal charts (chartId ends with "-modal"), show all lines from the same section
        // Otherwise, only show lines that match this specific chart
        // IMPORTANT: Also check if line belongs to the same metric section for split mode
        // In non-split mode: chartId = "acceleration-combined"
        // In split mode: chartId = "acceleration-x-axis-acceleration"
        // Harmonics created in non-split have chartId "acceleration-combined" but should also appear in split charts
        const extractBaseMetric = (id: string | undefined) => {
          if (!id) return null;
          // Remove "-combined" suffix or sensor-specific suffix to get base metric
          return id.replace(/-combined$/, '').split('-')[0];
        };

        const lineBaseMetric = extractBaseMetric(line.chartId);
        const currentBaseMetric = extractBaseMetric(chartId);

        const belongsToThisChart =
          !line.chartId || // No chartId = global line, show on all charts
          line.chartId === chartId || // Exact match
          (lineBaseMetric && currentBaseMetric && lineBaseMetric === currentBaseMetric) || // Same metric, different split
          (chartId?.endsWith('-modal') && line.chartId?.startsWith(chartId.replace('-modal', ''))); // Modal handling

        return hasValidTimestamp && belongsToThisChart;
      }),
    [allVerticalLines, chartId]
  );

  // Helper function to convert timestamp to x-position for Plotly shapes
  const getXPositionForShape = React.useCallback((timestamp: Date | number | string): string | number => {
    if (timestamp instanceof Date) {
      // Date axis: convert to ISO string format for Plotly
      return `${timestamp.getFullYear()}-${String(timestamp.getMonth() + 1).padStart(2, '0')}-${String(
        timestamp.getDate()
      ).padStart(2, '0')} ${String(timestamp.getHours()).padStart(2, '0')}:${String(timestamp.getMinutes()).padStart(
        2,
        '0'
      )}:${String(timestamp.getSeconds()).padStart(2, '0')}`;
    } else if (typeof timestamp === 'number') {
      // Numeric axis: return as number
      return timestamp;
    } else {
      // String/category axis: return as string
      return String(timestamp);
    }
  }, []);

  // Memoize vertical shapes separately from filtering
  const verticalShapes = React.useMemo(() => {
    // Find the color and prefix of the selected harmonic group
    const selectedLine = selectedHarmonicLineId
      ? validVerticalLines.find((l) => l.id === selectedHarmonicLineId)
      : null;
    const selectedGroupColor = selectedLine?.color;

    // Extract the prefix from the selected line (e.g., "H" from "H0", "C" from "C1", etc.)
    const selectedLinePrefix = selectedLine?.label?.match(/^-?([A-Z]+)\d+/)?.[1];

    // Find the first non-harmonic marker (sorted by timestamp) to use as default benchmark
    const nonHarmonicMarkers = validVerticalLines
      .filter((l) => !l.isHarmonic)
      .sort((a, b) => {
        const aTime = typeof a.timestamp === 'number' ? a.timestamp : a.timestamp.getTime();
        const bTime = typeof b.timestamp === 'number' ? b.timestamp : b.timestamp.getTime();
        return aTime - bTime;
      });
    const firstMarkerId = nonHarmonicMarkers.length > 0 ? nonHarmonicMarkers[0].id : null;

    return validVerticalLines
      .filter((line) => !line.isCustomLine)
      .flatMap((line, index) => {
        // Use line's assigned color for harmonics, otherwise use default
        const shouldUseAssignedColor = line.isHarmonic;
        let lineColor = shouldUseAssignedColor ? line.color || lineDefaultColor : lineDefaultColor;

        // Check if this is the selected marker - compare by timestamp ISO string
        // Also check if this marker is in the selectedTimestampsSet (for multi-select via chips)
        const lineTimestampISO =
          line.timestamp instanceof Date
            ? line.timestamp.toISOString()
            : typeof line.timestamp === 'number'
            ? new Date(line.timestamp).toISOString()
            : String(line.timestamp);

        // Helper to normalize timestamps for comparison (truncate microseconds to milliseconds)
        // Backend timestamps have microseconds (6 digits), JS Date has milliseconds (3 digits)
        const normalizeTimestamp = (ts: string): string => {
          const match = ts.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3})\d*Z$/);
          if (match) {
            return match[1] + 'Z';
          }
          return ts;
        };

        // Check if timestamp is in selectedTimestampsSet (by ID, timestamp ISO, or normalized timestamp)
        let isInSelectedSet = false;
        if (selectedTimestampsSet && selectedTimestampsSet.size > 0) {
          // First check by line ID (handler stores item IDs, not timestamps)
          if (line.id && selectedTimestampsSet.has(line.id)) {
            isInSelectedSet = true;
          } else if (selectedTimestampsSet.has(lineTimestampISO)) {
            isInSelectedSet = true;
          } else {
            // Check normalized match
            const normalizedLineTs = normalizeTimestamp(lineTimestampISO);
            for (const selectedTs of selectedTimestampsSet) {
              if (normalizeTimestamp(selectedTs) === normalizedLineTs) {
                isInSelectedSet = true;
                break;
              }
            }
          }
        }

        const isSelectedMarker = (selectedMarkerLabel && line.label === selectedMarkerLabel) || isInSelectedSet;

        // If selected, color it purple (regardless of mode)
        if (isSelectedMarker) {
          lineColor = '#7071F3'; // Primary purple color
        }

        // Check if this line belongs to the selected harmonic/side bands group
        // Must match both color AND prefix (e.g., both "H" or both "C")
        const linePrefix = line.label?.match(/^-?([A-Z]+)\d+/)?.[1];
        const isSelectedGroup =
          selectedGroupColor &&
          selectedLinePrefix &&
          line.isHarmonic &&
          line.color === selectedGroupColor &&
          linePrefix === selectedLinePrefix;

        // If line is part of selected group, color it purple (but selected marker takes priority)
        if (!isSelectedMarker && isSelectedGroup) {
          lineColor = '#7071F3'; // Primary purple color
        }

        // First harmonic (starting point) or benchmark line should be solid
        // For non-harmonic lines:
        // - Selected markers are always solid (purple + solid line)
        // - If benchmark is set, ONLY the benchmark should be solid
        // - If NO benchmark is set, the first marker should be solid (auto-benchmark)
        const isBenchmark = benchmarkLineId && line.id === benchmarkLineId;
        const isFirstMarker = !line.isHarmonic && line.id === firstMarkerId;
        const isSolid = isSelectedMarker || line.isFirstHarmonic || isBenchmark || (!benchmarkLineId && isFirstMarker);

        if (!line.isHarmonic) {
        }

        if (isBenchmark) {
        }
        if (!benchmarkLineId && isFirstMarker) {
        }

        // Apply opacity when focus is active (but not to selected group)
        // Dim ALL lines except the focused harmonic group, focused custom group, or selected group
        // Check both groupId (new) and color (fallback for old carriers/harmonics without groupId)
        const lineIdentifier = line.groupId || line.color;

        // Determine if this line belongs to a focused custom group
        const effectiveFocusedCustomGroupId = localFocusedCustomGroupId || focusedCustomGroupId;
        let isPartOfFocusedCustomGroup = false;
        if (effectiveFocusedCustomGroupId) {
          // Check if this line belongs to the focused custom group
          if (typeof line.label === 'string' && line.label.includes('|')) {
            // User-added line: check encoded groupId
            const parts = line.label.split('|');
            isPartOfFocusedCustomGroup = parts[1] === effectiveFocusedCustomGroupId;
          } else if (line._systemGroupId) {
            // System line: check _systemGroupId
            isPartOfFocusedCustomGroup = line._systemGroupId === effectiveFocusedCustomGroupId;
          }
        }

        // Check if this line is part of a custom group (has encoded label or system group)
        const isCustomGroupLine = (typeof line.label === 'string' && line.label.includes('|')) || line._systemGroupId;

        // Check if line is manually dimmed by user via menu (for non-deletable lines)
        const isManuallyDimmed = line.dimmed === true;

        const shouldDim =
          isManuallyDimmed || // Always dim if manually dimmed by user
          (!isSelectedGroup &&
            !isPartOfFocusedCustomGroup &&
            ((focusedHarmonicColor && (!line.isHarmonic || lineIdentifier !== focusedHarmonicColor)) ||
              // When a custom group is focused, dim ALL other lines (both regular markers and other custom groups)
              (effectiveFocusedCustomGroupId && (!isCustomGroupLine || !isPartOfFocusedCustomGroup))));

        if (shouldDim) {
          // Convert hex color to rgba with 0.1 opacity (more transparent)
          const hex = lineColor.replace('#', '');
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          lineColor = `rgba(${r}, ${g}, ${b}, 0.1)`;
        } else if (focusedHarmonicColor && line.isHarmonic) {
        }

        // Convert timestamp to appropriate x-position based on data type
        const xPos = getXPositionForShape(line.timestamp);

        return [
          {
            type: 'line' as const,
            x0: xPos,
            x1: xPos,
            y0: 0,
            y1: 1,
            yref: 'paper' as const,
            line: {
              color: lineColor,
              width: 1.7,
              dash: (isSolid ? 'solid' : 'dash') as 'solid' | 'dash' | 'dot' | 'longdash' | 'dashdot' | 'longdashdot',
            },
          },
        ];
      });
  }, [
    validVerticalLines,
    lineDefaultColor,
    focusedHarmonicColor,
    benchmarkLineId,
    selectedHarmonicLineId,
    selectedMarkerLabel,
    selectedTimestampsSet,
    getXPositionForShape,
    localFocusedCustomGroupId,
    focusedCustomGroupId,
  ]);

  // Teardrop shapes disabled - just show the label box without teardrop/circle shape
  const triangleShapes = React.useMemo(() => {
    return [] as any[];
  }, []);

  // Map pin shapes disabled - just show the label box without teardrop shape
  const mapPinShapes = React.useMemo(() => {
    return [] as any[];
  }, []);

  // Memoize interval lines processing - MUST be before early returns
  const { validIntervalLines, intervalShapes } = React.useMemo(() => {
    const validLines = intervalLines.filter((line) => {
      // Check if line has valid startTime (can be Date or number)
      if (line.startTime instanceof Date) {
        return !isNaN(line.startTime.getTime());
      }
      return typeof line.startTime === 'number' && !isNaN(line.startTime);
    });

    const intervalShapesArray = validLines.map((line) => {
      let lineColor = line.color || '#ff4444';

      // Dim interval lines when focus is active
      if (focusedHarmonicColor) {
        // Convert hex color to rgba with 0.1 opacity (more transparent)
        const hex = lineColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        lineColor = `rgba(${r}, ${g}, ${b}, 0.1)`;
      }

      // Convert startTime to appropriate x-position based on data type
      const xPos = getXPositionForShape(line.startTime);

      return {
        type: 'line' as const,
        x0: xPos,
        x1: xPos,
        y0: 0,
        y1: 1,
        yref: 'paper' as const,
        line: {
          color: lineColor,
          width: 2,
          dash: 'dash' as 'solid' | 'dash' | 'dot' | 'longdash' | 'dashdot' | 'longdashdot',
        },
      };
    });

    return { validIntervalLines: validLines, intervalShapes: intervalShapesArray };
  }, [intervalLines, focusedHarmonicColor, getXPositionForShape]);

  // Memoize vertical line annotations - MUST be before early returns
  const verticalLineAnnotations = React.useMemo(() => {
    // Find the color and prefix of the selected harmonic group
    const selectedLine = selectedHarmonicLineId
      ? validVerticalLines.find((l) => l.id === selectedHarmonicLineId)
      : null;
    const selectedGroupColor = selectedLine?.color;

    // Extract the prefix from the selected line (e.g., "H" from "H0", "C" from "C1", etc.)
    const selectedLinePrefix = selectedLine?.label?.match(/^-?([A-Z]+)\d+/)?.[1];

    return validVerticalLines.flatMap((line) => {
      // Helper function to format x-axis value for display (supports dates, numbers, and strings)
      const formatXValue = (timestamp: any): string => {
        // Check if it's a Date object or can be converted to a valid date
        const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
        const isValidDate = !isNaN(date.getTime());

        if (isValidDate && (timestamp instanceof Date || typeof timestamp === 'string')) {
          // It's a date - format as "Sep 5 07:56"
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const month = monthNames[date.getMonth()];
          const day = date.getDate();
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          return `${month} ${day} ${hours}:${minutes}`;
        } else if (typeof timestamp === 'number') {
          // It's a numeric value - format with appropriate precision
          return timestamp % 1 === 0 ? timestamp.toString() : timestamp.toFixed(2);
        } else {
          // It's a string or other type - display as-is
          return String(timestamp);
        }
      };

      // Get formatted x-axis value for display (works for Date, number, or string)
      // For custom lines from users, show username instead
      let xValueDisplay;
      if (line.isCustomLine && line.username) {
        // Always show username for custom lines if available
        xValueDisplay = `👤 ${line.username}`;
      } else if (line.isCustomLine && line.source === 'user') {
        // Fallback for user lines without username
        xValueDisplay = `👤 User`;
      } else {
        xValueDisplay = formatXValue(line.timestamp);
      }

      // Convert timestamp to appropriate x-position based on data type
      const xPosition = getXPositionForShape(line.timestamp);

      const annotations = [];

      // Check if this line belongs to the selected harmonic/side bands group
      // Must match both color AND prefix (e.g., both "H" or both "C")
      const linePrefix = line.label?.match(/^-?([A-Z]+)\d+/)?.[1];
      const isSelectedGroup =
        selectedGroupColor &&
        selectedLinePrefix &&
        line.isHarmonic &&
        line.color === selectedGroupColor &&
        linePrefix === selectedLinePrefix;

      // Check if this is the selected marker - compare by label AND timestamp ISO
      const annotationLineTimestampISO =
        line.timestamp instanceof Date
          ? line.timestamp.toISOString()
          : typeof line.timestamp === 'number'
          ? new Date(line.timestamp).toISOString()
          : String(line.timestamp);

      // Helper to normalize timestamps for comparison (truncate microseconds to milliseconds)
      const normalizeTs = (ts: string): string => {
        const match = ts.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3})\d*Z$/);
        return match ? match[1] + 'Z' : ts;
      };

      // Check if timestamp is in selectedTimestampsSet (by ID, timestamp ISO, or normalized timestamp)
      let isAnnotationInSelectedSet = false;
      if (selectedTimestampsSet && selectedTimestampsSet.size > 0) {
        // First check by line ID (handler stores item IDs, not timestamps)
        if (line.id && selectedTimestampsSet.has(line.id)) {
          isAnnotationInSelectedSet = true;
        } else if (selectedTimestampsSet.has(annotationLineTimestampISO)) {
          isAnnotationInSelectedSet = true;
        } else {
          const normalizedLineTs = normalizeTs(annotationLineTimestampISO);
          for (const selectedTs of selectedTimestampsSet) {
            if (normalizeTs(selectedTs) === normalizedLineTs) {
              isAnnotationInSelectedSet = true;
              break;
            }
          }
        }
      }

      const isSelectedMarker = (selectedMarkerLabel && line.label === selectedMarkerLabel) || isAnnotationInSelectedSet;

      // Harmonic lines: label at top, User markers: label at top, System lines from backend: label at top
      // Only old "compare" lines (no markerType) show timestamp in middle
      const isHarmonic = line.isHarmonic;
      const isFirstHarmonic = line.isFirstHarmonic;
      const isUserMarker = line.markerType === 'user';
      const isSystemMarker = line.markerType === 'system' || line.source === 'system';
      const showLabelAtTop = isHarmonic || isUserMarker || isSystemMarker;

      // Background colors
      let bgColor, borderColor;
      if (isSelectedMarker || isSelectedGroup) {
        // Selected: purple background with purple border (ALL marker types including system)
        bgColor = 'rgba(112, 113, 243, 0.95)'; // Primary purple
        borderColor = '#7071F3'; // Primary purple
      } else if (isHarmonic) {
        // All harmonics in same group get same background color based on line.color
        const hexToRgba = (hex: string, alpha: number) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };
        bgColor = hexToRgba(line.color, 0.95);
        borderColor = line.color;
      } else if (isUserMarker || isSystemMarker) {
        // User/System markers: dark background with bright border to indicate clickable
        bgColor = isDarkMode ? 'rgba(31, 41, 55, 0.95)' : 'rgba(15, 23, 42, 0.95)';
        borderColor = lineDefaultColor;
      } else {
        // Compare lines: default background
        bgColor = isDarkMode ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)';
        borderColor = isDarkMode ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)';
      }

      // Text color
      let textColor;
      if (isSelectedMarker || isSelectedGroup) {
        textColor = '#ffffff'; // White text for purple background
      } else if (isHarmonic || isUserMarker || isSystemMarker) {
        textColor = '#ffffff'; // White text for all harmonics and markers
      } else {
        textColor = '#4b5563'; // Gray for compare lines
      }

      // Determine if this line belongs to a focused custom group
      const effectiveFocusedCustomGroupId = localFocusedCustomGroupId || focusedCustomGroupId;
      let isPartOfFocusedCustomGroup = false;
      if (effectiveFocusedCustomGroupId) {
        // Check if this line belongs to the focused custom group
        if (typeof line.label === 'string' && line.label.includes('|')) {
          // User-added line: check encoded groupId
          const parts = line.label.split('|');
          isPartOfFocusedCustomGroup = parts[1] === effectiveFocusedCustomGroupId;
        } else if (line._systemGroupId) {
          // System line: check _systemGroupId
          isPartOfFocusedCustomGroup = line._systemGroupId === effectiveFocusedCustomGroupId;
        }
      }

      // Check if this line is actually a custom group line (not a regular marker)
      const isPartOfCustomGroup =
        line.label && typeof line.label === 'string' && (line.label.includes('|') || line._systemGroupId);

      // Check if line is manually dimmed by user via menu
      const isManuallyDimmed = line.dimmed === true;

      // Apply focus dimming to ALL non-focused lines (harmonics and compare lines), but not to selected marker or selected group
      // Check both groupId (new) and color (fallback for old carriers/harmonics without groupId)
      const annotationLineIdentifier = line.groupId || line.color;
      const shouldDimAnnotation =
        isManuallyDimmed || // Always dim annotation if manually dimmed by user
        (!isSelectedMarker &&
          !isSelectedGroup &&
          ((focusedHarmonicColor && (!isHarmonic || annotationLineIdentifier !== focusedHarmonicColor)) ||
            // When a custom group is focused, dim ALL other lines (both regular markers and other custom groups)
            (effectiveFocusedCustomGroupId && (!isPartOfCustomGroup || !isPartOfFocusedCustomGroup))));

      if (shouldDimAnnotation) {
        // Helper function to convert any color to rgba with opacity
        const applyOpacity = (color: string, opacity: number) => {
          if (color.startsWith('rgba')) {
            // Already rgba, replace the opacity
            return color.replace(/[\d.]+\)$/, `${opacity})`);
          } else if (color.startsWith('rgb')) {
            // rgb to rgba
            return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
          } else if (color.startsWith('#')) {
            // hex to rgba
            const hex = color.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
          }
          return color; // fallback
        };

        // Apply lower opacity to all colors (more transparent)
        textColor = applyOpacity(textColor, 0.2);
        bgColor = applyOpacity(bgColor, 0.2);
        borderColor = applyOpacity(borderColor, 0.15);
      }

      // Primary annotation - label name (always shown)
      const labelAnnotation: any = {
        x: xPosition,
        y: showLabelAtTop ? 0.98 : 0.5, // Top for harmonics/user/system markers, middle for old compare lines
        xref: 'x' as const,
        yref: 'paper' as const,
        text: showLabelAtTop ? line.label : xValueDisplay, // Custom label for markers, x-value for old compare
        showarrow: false,
        textangle: showLabelAtTop ? 0 : -90, // Horizontal for markers, vertical for old compare
        font: {
          size: 11,
          color: textColor,
          family: 'monospace',
        },
        xanchor: showLabelAtTop ? 'center' : 'right', // Center for markers, right for old compare
        yanchor: showLabelAtTop ? 'bottom' : 'middle', // Bottom anchor = label sits at marker position
        bgcolor: bgColor,
        bordercolor: borderColor,
        // Thicker border for clickable items to indicate they're actionable
        borderwidth: (isHarmonic && isFirstHarmonic) || isUserMarker || isSystemMarker ? 2 : 1,
        borderpad: 3,
        // Make ALL harmonics, user markers, system markers, AND old compare lines clickable
        captureevents: true,
        // Store line info for click detection
        name: isHarmonic
          ? `harmonic-${line.id}`
          : isUserMarker || isSystemMarker
          ? `map-pin-${line.id}`
          : `vertical-line-${line.id}`,
      };

      // Add border radius for all labels (markers get larger radius)
      labelAnnotation.borderradius = showLabelAtTop ? 12 : 4;

      annotations.push(labelAnnotation);

      // Secondary annotation - x-value on x-axis (only when showDualLabels is true and for harmonics/user markers)
      if (showDualLabels && (isHarmonic || isUserMarker)) {
        const xValueAnnotation: any = {
          x: xPosition,
          y: -0.01, // Bottom of chart with small gap from x-axis
          xref: 'x' as const,
          yref: 'paper' as const,
          text: xValueDisplay,
          showarrow: false,
          textangle: -90, // Vertical
          font: {
            size: 8,
            color: isDarkMode ? '#cbd5e0' : '#55687d', // Secondary text color
            family: 'monospace',
          },
          xanchor: 'center',
          yanchor: 'top', // Top anchor = label sits below the line
          bgcolor: isDarkMode ? 'rgba(31, 41, 55, 0.8)' : 'rgba(255, 255, 255, 0.8)',
          bordercolor: isDarkMode ? 'rgba(74, 85, 104, 0.5)' : 'rgba(229, 231, 235, 0.8)',
          borderwidth: 1,
          borderpad: 2,
          borderradius: 4,
          // Not clickable (only the primary label is clickable)
          captureevents: false,
          name: `x-value-${line.id}`, // Different name to distinguish from primary label
        };

        // Apply focus dimming to x-value annotation as well
        if (shouldDimAnnotation) {
          const applyOpacity = (color: string, opacity: number) => {
            if (color.startsWith('rgba')) {
              return color.replace(/[\d.]+\)$/, `${opacity})`);
            } else if (color.startsWith('rgb')) {
              return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
            } else if (color.startsWith('#')) {
              const hex = color.replace('#', '');
              const r = parseInt(hex.substring(0, 2), 16);
              const g = parseInt(hex.substring(2, 4), 16);
              const b = parseInt(hex.substring(4, 6), 16);
              return `rgba(${r}, ${g}, ${b}, ${opacity})`;
            }
            return color;
          };

          xValueAnnotation.font.color = applyOpacity(xValueAnnotation.font.color, 0.2);
          xValueAnnotation.bgcolor = applyOpacity(xValueAnnotation.bgcolor, 0.2);
          xValueAnnotation.bordercolor = applyOpacity(xValueAnnotation.bordercolor, 0.15);
        }

        annotations.push(xValueAnnotation);
      }

      return annotations;
    });
  }, [
    validVerticalLines,
    enabledSensors,
    isDarkMode,
    focusedHarmonicColor,
    selectedHarmonicLineId,
    getXPositionForShape,
    showDualLabels,
    localFocusedCustomGroupId,
    focusedCustomGroupId,
  ]);

  // *** MOVED BEFORE EARLY RETURNS: Calculate shapes (must happen unconditionally for hooks) ***

  // Filter thresholds for this chart
  const relevantThresholds = showThresholds
    ? (thresholds ?? []).filter((threshold) => {
        if (threshold.value === null || threshold.value === undefined) return false;
        if (!threshold.visible) return false;
        if (sensorName && threshold.sensorName !== sensorName) return false;
        return true;
      })
    : [];

  // Helper to convert hex color to rgba
  const hexToRgba = (hex: string, alpha: number): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return `rgba(255, 0, 0, ${alpha})`;
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Generate threshold shapes based on variant
  const thresholdShapes = React.useMemo(() => {
    if (thresholdVariant === 'flat') {
      // Original behavior - just dashed lines
      return relevantThresholds.map((threshold) => ({
        type: 'line' as const,
        x0: 0,
        x1: 1,
        xref: 'paper' as const,
        y0: threshold.value,
        y1: threshold.value,
        line: {
          color: threshold.color || '#FF0000',
          width: 1,
          dash: 'dash' as 'dash',
        },
      }));
    }

    // For bands-fill and bands-gradient, create filled rectangles
    // Sort thresholds by value ascending
    const sortedThresholds = [...relevantThresholds].sort((a, b) => a.value - b.value);

    const shapes: any[] = [];

    sortedThresholds.forEach((threshold, index) => {
      const y0 = index === 0 ? 0 : sortedThresholds[index - 1].value;
      const y1 = threshold.value;

      // Determine fill opacity based on variant
      const fillOpacity = thresholdVariant === 'bands-gradient' ? 0.15 : 0.2;

      // Add the filled rectangle (band)
      shapes.push({
        type: 'rect' as const,
        x0: 0,
        x1: 1,
        xref: 'paper' as const,
        y0,
        y1,
        yref: 'y' as const,
        fillcolor: hexToRgba(threshold.color || '#FF0000', fillOpacity),
        line: { width: 0 },
        layer: 'below' as const,
      });

      // For gradient variant, add additional rectangles with increasing opacity toward the line
      if (thresholdVariant === 'bands-gradient') {
        const bandHeight = y1 - y0;
        const gradientSteps = 3;
        for (let step = 1; step <= gradientSteps; step++) {
          const stepY0 = y0 + (bandHeight * (step - 1)) / gradientSteps;
          const stepY1 = y0 + (bandHeight * step) / gradientSteps;
          // Stronger color near the line (top), fades toward bottom
          const stepOpacity = 0.01 + step * 0.06; // 0.07 -> 0.13 -> 0.19

          shapes.push({
            type: 'rect' as const,
            x0: 0,
            x1: 1,
            xref: 'paper' as const,
            y0: stepY0,
            y1: stepY1,
            yref: 'y' as const,
            fillcolor: hexToRgba(threshold.color || '#FF0000', stepOpacity),
            line: { width: 0 },
            layer: 'below' as const,
          });
        }
      }

      // Add the threshold line on top
      shapes.push({
        type: 'line' as const,
        x0: 0,
        x1: 1,
        xref: 'paper' as const,
        y0: threshold.value,
        y1: threshold.value,
        line: {
          color: threshold.color || '#FF0000',
          width: 1,
          dash: 'dash' as 'dash',
        },
      });
    });

    return shapes;
  }, [relevantThresholds, thresholdVariant]);

  const thresholdLabels: Partial<Plotly.Annotations>[] = relevantThresholds.map((t) => ({
    xref: 'paper' as const,
    yref: 'y' as const,
    x: 1,
    y: t.value,
    xanchor: 'right' as const,
    yanchor: 'bottom' as const,
    text: `${t.label}: ${t.value}`,
    showarrow: false,
    font: {
      size: 10,
      color: t.color || '#FF0000',
    },
  }));

  const chartSelectedRanges = selectedRanges.filter((range) => range.chartId === chartId);
  const selectionShapes = chartSelectedRanges.flatMap((range) => [
    {
      type: 'line' as const,
      x0: range.startTime,
      x1: range.startTime,
      y0: 0,
      y1: 1,
      yref: 'paper' as const,
      line: {
        color: '#FFD700',
        width: 2,
      },
    },
    {
      type: 'line' as const,
      x0: range.endTime,
      x1: range.endTime,
      y0: 0,
      y1: 1,
      yref: 'paper' as const,
      line: {
        color: '#FFD700',
        width: 2,
      },
    },
    {
      type: 'rect' as const,
      x0: range.startTime,
      x1: range.endTime,
      y0: 0,
      y1: 1,
      yref: 'paper' as const,
      fillcolor: 'rgba(255, 215, 0, 0.2)',
      line: {
        width: 0,
      },
    },
  ]);

  // console.log('data in plotly', data);
  /**
   * Temporary frontend logic for VisualAI charts.
   * Will be removed once the backend provides this data.
   */
  const { shapes: levelShapes, annotations: levelAnnotations } = React.useMemo(
    () =>
      buildVisualAILevelShapes({
        levels: data?.shapes?.levels,
        isMirrored: Boolean(data?.shapes?.isMirrored),
        shouldCalculate: Boolean(data?.shapes?.shouldCalculate),
        yValues: [...(data?.data?.[0]?.y ?? []), ...(data?.data?.[1]?.y ?? [])].filter((v): v is number =>
          Number.isFinite(v)
        ),
      }),
    [data?.shapes?.levels, data?.shapes?.isMirrored, data?.shapes?.shouldCalculate, data?.data]
  );

  /**
   * Temporary frontend logic for VisualAI charts.
   * Will be removed once the backend provides this data.
   */
  const { visualAIPlaylistItems, visualAIActiveItemIndex } = useSelector((state: RootState) => state.assetsStudio);
  const { shapes: evidenceShapes, images: evidenceImages } = React.useMemo(() => {
    const selectedEvidence = visualAIPlaylistItems[visualAIActiveItemIndex];

    if (!isVisualAI || !visualAIPlaylistItems) {
      return { shapes: [], images: [] };
    }

    const { yMin, yMax } = getCombinedYRangeForVisualAI({
      data: data?.data ?? [],
      levelShapes,
      isMirrored: Boolean(data?.shapes?.isMirrored),
    });

    return buildEvidenceMarkers({
      evidences: visualAIPlaylistItems,
      selectedEvidence,
      graphType: data?.id,
      yRange: { yMin, yMax },
      isMirrored: Boolean(data?.shapes?.isMirrored),
    });
  }, [isVisualAI, visualAIPlaylistItems, visualAIActiveItemIndex, data?.id, data?.data, data?.shapes, levelShapes]);

  // Create annotations for custom line icons
  const customLineAnnotations = React.useMemo(() => {
    const focusedGroup = localFocusedCustomGroupId || focusedCustomGroupId;

    return allVerticalLines
      .filter((line) => {
        // Show custom lines based on sync settings
        if (!line.isCustomLine || !line.groupIcon) return false;

        // If synced, show all custom lines on all charts
        if (syncCustomLines) return true;

        // If not synced, only show lines for this chart
        return line.chartId === chartId;
      })
      .map((line) => {
        // Get the correct x position using the same function as the lines
        const xPos = getXPositionForShape(line.timestamp);

        // Determine if this icon should be dimmed
        const isInFocusedGroup = focusedGroup && line.groupId === focusedGroup;
        const shouldDimIcon = focusedGroup && !isInFocusedGroup;

        return {
          x: xPos,
          y: 0.98,
          yref: 'paper',
          text: line.groupIcon,
          showarrow: false,
          font: {
            size: 16,
            color: shouldDimIcon ? 'rgba(85, 104, 125, 0.6)' : undefined, // Dim the icon color when not focused
          },
          opacity: shouldDimIcon ? 0.6 : 1, // Also apply opacity to the entire annotation
          xanchor: 'center',
          yanchor: 'bottom',
          captureevents: true,
          name: `custom-line-${line.id}`, // Add name for click detection
          customdata: {
            lineId: line.id,
            groupId: line.groupId,
            isCustomLine: true,
            groupIcon: line.groupIcon,
            label: line.label || line.displayLabel,
            source: line.source,
            timestamp: line.timestamp,
            username: line.username,
            displayLabel: line.displayLabel,
            _systemGroupLabel: line._systemGroupLabel,
          },
        };
      });
  }, [allVerticalLines, localFocusedCustomGroupId, focusedCustomGroupId, syncCustomLines, chartId]);

  // Removed customLineUserAnnotations - no longer showing username labels below icons

  const annotations = [...thresholdLabels, ...verticalLineAnnotations, ...customLineAnnotations, ...levelAnnotations];

  const images = [...evidenceImages];

  // Calculate hasBarChart before early returns (safe to check even if data not loaded)
  const hasBarChart = React.useMemo(() => {
    if (!data?.data || !Array.isArray(data.data)) return false;
    return data.data.some((trace: any) => trace.type === 'bar');
  }, [data]);

  // Define colors based on theme (needed for layout)
  const chartColors = {
    text: isDarkMode ? '#f7fafc' : '#01152b',
    textSecondary: isDarkMode ? '#cbd5e0' : '#55687d',
    grid: isDarkMode ? '#4a5568' : '#e6e8ec',
    background: isDarkMode ? '#2d3748' : 'white',
    paper: isDarkMode ? '#1a202c' : 'white',
  };

  // Determine Y-axis range based on chart type
  const getYAxisRange = () => {
    // For split charts, use individual calculated range
    let range: [number, number] | null;
    if (isSplitChart) {
      range = originalYRange;
    } else {
      // For combined charts, use globalYRange if provided, otherwise calculated range
      range = globalYRange ?? originalYRange;
    }

    // Spectrum charts: ensure Y-axis doesn't show negative values (data is always positive)
    // Use Math.max to prevent negative axis values, but don't force zero inclusion
    if (isSpectrum && range) {
      return [Math.max(0, range[0]), range[1]] as [number, number];
    }
    // VisualAI charts: force start at 0 and include all shapes
    if (isVisualAI) {
      const { yMin, yMax } = getCombinedYRangeForVisualAI({
        data: data?.data ?? [],
        levelShapes,
        isMirrored: Boolean(data?.shapes?.isMirrored),
      });
      return [yMin, yMax];
    }
    return range;
  };

  // Memoize base layout to ensure it updates when shapes, annotations, or benchmark changes
  const baseLayout = React.useMemo(() => {
    const layoutObj = {
      title: {
        text: `${title} ${showUnits && unit ? '[' + unit + ']' : ''}`,
        font: {
          family: 'Montserrat, sans-serif',
          size: 12,
          weight: 500,
          color: chartColors.text,
        },
        x: 0,
        y: 0.999,
        xanchor: 'left' as const,
        yanchor: 'top' as const,
        xref: 'container' as const,
        yref: 'container' as const,
      },
      xaxis: {
        title: '',
        showgrid: true,
        gridcolor: chartColors.grid,
        color: chartColors.textSecondary,
        tickcolor: chartColors.textSecondary,
        tickfont: {
          size: 11,
          color: chartColors.textSecondary,
          family: 'system-ui, -apple-system, sans-serif',
        },
        showline: false,
        zeroline: false,
        type: xAxisType as any, // Use detected x-axis type instead of hardcoded 'date'
        constrain: xAxisType === 'date' && !isWaveform ? ('range' as const) : undefined,
        uirevision: `preserve-zoom-${xAxisType}`, // Reset axis state when xAxisType transitions (date→numeric)
        //  uirevision: isVisualAI ? 'unchanged' : 'preserve-zoom', // Preserve zoom state when layout changes
        // For numeric axes with k-notation, set explicit range to fit data tightly
        // Store the initial range and reuse it to preserve zoom when shapes change
        range: (() => {
          if (isWaveform || isSpectrum) {
            return undefined;
          }
          if (xAxisType === 'date') {
            return timeRange;
          }
          // For numeric axes, calculate and store initial range once
          if (xAxisKNotationConfig) {
            if (!initialXRangeRef.current) {
              initialXRangeRef.current = [xAxisKNotationConfig.minX * 0.98, xAxisKNotationConfig.maxX * 1.02];
            }
            // Always return the same stored range - uirevision will preserve user zoom
            return initialXRangeRef.current;
          }
          return undefined;
        })(),
        constraintrange: xAxisType === 'date' && !isWaveform ? [start, end] : undefined,
        autorange: isWaveform || isSpectrum ? true : xAxisType === 'date' ? false : xAxisKNotationConfig ? false : true,
        // Use custom k-notation (0.5k, 1k, 2k, etc.) for numeric axes with values >= 1000
        ...(xAxisKNotationConfig && {
          tickmode: 'array' as const,
          tickvals: xAxisKNotationConfig.tickvals,
          ticktext: xAxisKNotationConfig.ticktext,
        }),
        // Spectrum: prevent negative X-axis values without forcing zero inclusion
        // 'nonnegative' ensures axis min >= 0 but doesn't constrain zoom like 'tozero'
        rangemode: isSpectrum ? 'nonnegative' : undefined,
        // Waveform: show time in mm:ss.ms format
        // Uses Plotly's date format since x-values are epoch-based Date ISO strings
        ...(isWaveform && {
          tickformat: waveformDuration && waveformDuration <= 10 ? '%M:%S.%L' : '%M:%S',
          nticks: waveformDuration && waveformDuration <= 10 ? 11 : 7, // More ticks for shorter durations
        }),
      },
      yaxis: {
        showgrid: true,
        gridcolor: chartColors.grid,
        color: chartColors.textSecondary,
        tickcolor: chartColors.textSecondary,
        showline: false,
        zeroline: false,
        fixedrange: false,
        uirevision: 'preserve-zoom', // Preserve zoom state when layout changes
        // Spectrum: let Plotly auto-fit Y-axis; other charts use calculated range
        autorange: isSpectrum ? true : data?.shapes?.isMirrored ? 'reversed' : getYAxisRange() ? false : true,
        range: isSpectrum ? undefined : getYAxisRange() ?? undefined,
        tickfont: {
          size: 11,
          color: chartColors.textSecondary,
          family: 'system-ui, -apple-system, sans-serif',
        },
        tickformat: getYAxisTickFormat(
          data?.data?.flatMap((trace: any) => (Array.isArray(trace.y) ? trace.y : [])) ?? []
        ),
        // Spectrum: prevent negative Y-axis values without forcing zero inclusion
        rangemode: isSpectrum ? 'nonnegative' : undefined,
      },

      autosize: true,
      plot_bgcolor: chartColors.background,
      paper_bgcolor: chartColors.paper,
      margin: { l: 35, r: 10, t: 20, b: 45 },
      height,
      showlegend: false,
      // shapes and annotations are merged in later, outside the useMemo
      hovermode: 'x unified' as const,
      // Force Plotly to redraw when shapes change, but preserve zoom
      // Include chartId to ensure each chart has its own stable revision
      // shapes.length is NOT included to preserve zoom when adding/removing markers, harmonics, or carriers
      // Selection modes are NOT included to preserve zoom when entering/exiting marking modes
      // benchmarkLineId is NOT included because it only changes line style (solid/dashed) and shouldn't reset zoom
      // Only include focusedHarmonicColor which dims non-focused lines (visual change that might need redraw)
      uirevision: `preserve-zoom-${chartId}-${focusedHarmonicColor || 'none'}`,
      ...(hasBarChart && {
        barmode: 'stack' as const,
        bargap: 0.1,
        bargroupgap: 0.05,
      }),
    };
    return layoutObj;
  }, [
    title,
    unit,
    showUnits,
    chartColors,
    timeRange,
    start,
    end,
    height,
    // REMOVED shapes from deps - uirevision preserves zoom when shapes change, no need to recreate layout
    // REMOVED annotations from deps - uirevision preserves zoom when annotations change
    // This prevents zoom reset when adding/removing vertical lines (markers/timestamps)
    focusedHarmonicColor,
    hasBarChart,
    data,
    getYAxisRange,
    xAxisType,
    xAxisKNotationConfig,
    dataLayer,
    // benchmarkLineId removed - only affects shape styles, not layout structure
    // Selection modes removed - they should not trigger layout recalculation
  ]);

  // Create shapes for custom vertical lines
  const customVerticalLineShapes = React.useMemo(() => {
    const customLines = allVerticalLines.filter((line) => line.isCustomLine);

    return customLines.map((line) => {
      const xPos = getXPositionForShape(line.timestamp);
      const focusedGroup = localFocusedCustomGroupId || focusedCustomGroupId;

      // Check if this line is selected by ID (same mechanism as regular vertical lines)
      const isSelectedById = line.id && selectedTimestampsSet && selectedTimestampsSet.has(line.id);

      // Determine if this line is in the focused group
      const isInFocusedGroup = focusedGroup && line.groupId === focusedGroup;

      // Check if manually dimmed by user via menu
      const isManuallyDimmed = line.dimmed === true;

      const shouldDimOthers = isManuallyDimmed || (focusedGroup && !isInFocusedGroup && !isSelectedById);

      // Set color and style based on selection and focus state
      let lineColor: string;
      let lineStyle: 'solid' | 'dash' | 'dot' | 'longdash' | 'dashdot' | 'longdashdot';

      if (isSelectedById && !isManuallyDimmed) {
        // Selected by ID: Purple solid line (highest priority, unless manually dimmed)
        lineColor = '#7071F3';
        lineStyle = 'solid';
      } else if (isInFocusedGroup && !isManuallyDimmed) {
        // Focused group: Purple solid lines like spectrum focus
        lineColor = '#7071F3';
        lineStyle = 'solid';
      } else if (shouldDimOthers) {
        // Dimmed: either manually or by focus on other group
        lineColor = 'rgba(85, 104, 125, 0.3)'; // Dimmed gray
        lineStyle = 'dash';
      } else {
        // Normal state: Regular gray dashed
        lineColor = '#55687d';
        lineStyle = 'dash';
      }

      return {
        type: 'line' as const,
        x0: xPos,
        x1: xPos,
        y0: 0,
        y1: 1,
        yref: 'paper' as const,
        line: {
          color: lineColor,
          width: 1.7,
          dash: lineStyle,
        },
      };
    });
  }, [allVerticalLines, localFocusedCustomGroupId, focusedCustomGroupId, selectedTimestampsSet]);

  // Combine all shapes (intervals + triangles + thresholds + selections + custom lines)
  const shapes = React.useMemo(() => {
    // Remove ALL teardrops if there are custom lines
    const hasCustomLines = allVerticalLines.some(
      (line) =>
        line.isCustomLine ||
        line._systemGroupId === 'unicorn-events' ||
        line._systemGroupId === 'teddy-events' ||
        line.groupIcon
    );

    const allShapes = [
      ...intervalShapes,
      ...(hasCustomLines ? [] : triangleShapes), // NO triangles if custom lines exist
      ...thresholdShapes,
      ...selectionShapes,
      ...verticalShapes,
      ...customVerticalLineShapes,
      ...(hasCustomLines ? [] : mapPinShapes), // NO map pins if custom lines exist
      ...levelShapes,
      ...evidenceShapes,
    ];

    // Add purple hover line when in pending mode (waiting for second click)
    if (isPendingMode && hoverXPosition !== null) {
      const hoverShape = {
        type: 'line' as const,
        x0: hoverXPosition,
        y0: 0,
        x1: hoverXPosition,
        y1: 1,
        yref: 'paper' as const,
        line: {
          color: '#9333ea', // Purple color
          width: 2,
          dash: 'dot' as const, // Dotted line to distinguish from actual markers
        },
        layer: 'above',
      };
      allShapes.push(hoverShape);
    }

    // Add waveform playback progress line (black vertical line)
    // CRITICAL: Waveform x-axis uses NUMERIC seconds (0, 0.00025, 0.0005, ...), not ISO strings
    if (isWaveform && waveformPlaybackTime !== undefined && waveformPlaybackTime >= 0) {
      const playheadShape = {
        type: 'line' as const,
        x0: waveformPlaybackTime, // Use numeric seconds directly
        y0: 0,
        x1: waveformPlaybackTime, // Use numeric seconds directly
        y1: 1,
        yref: 'paper' as const,
        line: {
          color: '#000000', // Black color
          width: 3,
        },
        layer: 'above',
      };
      allShapes.push(playheadShape);
    }

    return allShapes;
  }, [
    intervalShapes,
    triangleShapes,
    thresholdShapes,
    selectionShapes,
    verticalShapes,
    isPendingMode,
    hoverXPosition,
    isWaveform,
    waveformPlaybackTime,
    evidenceShapes,
  ]);

  // CRITICAL: Memoize final layout with dragmode to prevent unnecessary recreations
  // Merge shapes and annotations OUTSIDE the useMemo to avoid stale closures
  // This ensures zoom is preserved when adding/removing vertical lines (uirevision handles it)
  const layout = React.useMemo(() => {
    return {
      ...baseLayout,
      shapes, // Merge shapes here so they update without recreating the base layout
      annotations, // Merge annotations here so they update without recreating the base layout
      dragmode: selectionMode ? 'select' : 'zoom',
      selectdirection: 'h',
      images, // Merge images here so they update without recreating the base layout
    };
  }, [baseLayout, selectionMode, shapes, annotations, images]);

  // CRITICAL FIX: Preserve zoom when changing drag mode (selection mode changes)
  // When dragmode changes, update it via relayout instead of replacing the entire layout
  // This prevents Plotly from resetting the zoom
  const previousSelectionModeRef = useRef(selectionMode);
  useEffect(() => {
    // Only run when selectionMode actually changes (not on initial render)
    if (previousSelectionModeRef.current !== selectionMode && plotRef.current?.el) {
      const plotElement = plotRef.current.el;

      // Use Plotly.relayout to update only dragmode, preserving current zoom
      if ((window as any).Plotly) {
        (window as any).Plotly.relayout(plotElement, {
          dragmode: selectionMode ? 'select' : 'zoom',
        }).catch((error: any) => {
          console.error('Failed to update dragmode:', error);
        });
      }

      previousSelectionModeRef.current = selectionMode;
    }
  }, [selectionMode]);

  // CRITICAL: Memoize config to prevent unnecessary recreations
  const plotConfig = React.useMemo(() => {
    return {
      displayModeBar: !isDiscrete,
      displaylogo: false,
      modeBarButtonsToRemove: selectionMode
        ? (['zoom2d'] as Partial<ModeBarDefaultButtons>[])
        : (['zoom2d', 'pan2d', 'select2d', 'lasso2d', 'hoverClosestCartesian'] as Partial<ModeBarDefaultButtons>[]),
      responsive: true,
      scrollZoom: false,
      doubleClick: (enableDoubleClickZoomReset ? 'reset' : false) as false | 'reset',
      doubleClickDelay: enableDoubleClickZoomReset ? 1000 : 100,
      showTips: false,
      staticPlot: false,
      editable: false,
      toImageButtonOptions: {
        format: 'png' as const,
        filename: `sensor_chart_${chartId}`,
        height: height,
        width: 800,
        scale: 1,
      },
    };
  }, [isDiscrete, selectionMode, enableDoubleClickZoomReset, chartId, height]);

  // *** END MOVED SECTION ***

  // Early returns for invalid states

  if (!anySensorsEnabled) {
    return (
      <ChartEmptyState
        type={EMPTY_STATE_ENUM.NO_SENSORS}
        className={splitColumns && splitColumns > 2 ? '!max-h-[180px]' : ''}
        dataLayer={dataLayer}
      />
    );
  }
  if (!isValidData) {
    return (
      <div>
        <div className="bg-white font-medium text-lg">{title}</div>
        <ChartEmptyState
          type={EMPTY_STATE_ENUM.NO_DATA}
          className={splitColumns && splitColumns > 2 ? 'min-h-[175px]' : ''}
          dataLayer={dataLayer}
        />
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-48 bg-gray-100 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
        <div className="text-[#55687d] dark:text-[#8b98a5]">
          Loading chart with {data?.data[0]?.x?.length || 0} data points...
        </div>
      </div>
    );
  }

  // Prepare Plotly data with comprehensive error handling
  const plotData: any[] = [];
  try {
    // SPECTRUM ONLY: Check if we're in expandable legend mode with chip_value support (multiple timestamps per sensor)
    // Only apply this logic for spectrum data layer to avoid affecting other charts
    const hasExpandableLegend = expandableLegendSeries && expandableLegendSeries.length > 0;
    const hasChipValues = isSpectrum && data.data.some((s: any) => s.chip_value);

    // CRITICAL: Use expandableLegendFocusMode to determine path, not hasChipValues
    // All sections in the same data layer should behave the same way
    const useSpectrumFocusPath = expandableLegendFocusMode && hasChipValues && hasExpandableLegend;

    if (useSpectrumFocusPath) {
      // SPECTRUM FOCUS MODE: Loop through all data entries to handle multiple timestamps per sensor
      // Each data entry represents one timestamp for one sensor (backend returns separate entries per timestamp)
      for (let index = 0; index < data.data.length; index++) {
        const sensor = data.data[index];
        const label = sensor.displayName || sensor.name;

        // Skip if sensor is not enabled
        if (!enabledSensors[label]) continue;

        // Find the series for this sensor (use 'label' property as per createExpandableLegendFromFocus)
        const seriesForSensor = expandableLegendSeries.find((s: any) => s.label === label || s.id === label);

        // CRITICAL: Don't skip sensors without a series - apply timestamp filtering to ALL enabled sensors
        // This ensures all sections (Fan Spectrum, High Resolution, Envelope) show their own sensors
        // while still filtering by the same selected timestamps

        // Get selected timestamps for this sensor
        let allTimestamps: any[] = [];
        let selectedTimestamps: any[] = [];
        let hasAnyEnabledTimestamps = false;

        if (seriesForSensor) {
          // If sensor has an expandable series, use its timestamps
          allTimestamps = seriesForSensor.timestamps || [];
          selectedTimestamps = allTimestamps.filter((t: any) => t.enabled);
          hasAnyEnabledTimestamps = selectedTimestamps.length > 0;
        } else if (expandableLegendFocusMode && selectedTimestampsSet && selectedTimestampsSet.size > 0) {
          // No expandable series for this sensor, but we're in focus mode - apply timestamp filtering using selectedTimestampsSet
          // This ensures ALL sections filter by the same timestamps even if they're not in expandableLegendSeries
          hasAnyEnabledTimestamps = true;
          // We'll use selectedTimestampsSet for filtering (converted to array later)
        }

        // If timestamps exist but none are enabled, don't filter by chip_value - show default data
        const shouldFilterByTimestamp = hasAnyEnabledTimestamps;

        // Match chip_value (ISO string from backend) to timestamp.timestamp (ISO string from expandable legend)
        let matchingTimestamp: any = null;
        if (sensor.chip_value && shouldFilterByTimestamp) {
          // Only filter by timestamp if we have enabled timestamps
          // Special handling for "Closest" / "Latest" / "0" variations
          const isLatestChipValue =
            sensor.chip_value === 'Closest' ||
            sensor.chip_value === '__latest__' ||
            sensor.chip_value === 'latest' ||
            sensor.chip_value === '0';

          if (seriesForSensor && selectedTimestamps.length > 0) {
            // Sensor has expandable series - use its timestamps
            if (isLatestChipValue) {
              // Match to Latest timestamp
              matchingTimestamp = selectedTimestamps.find(
                (t: any) => t.id === '__latest__' || t.id === 'latest' || t.id === '0' || t.label === 'Latest'
              );
            } else {
              // Match by ISO timestamp string
              matchingTimestamp = selectedTimestamps.find((t: any) => t.timestamp === sensor.chip_value);
            }

            if (!matchingTimestamp) {
              // This specific timestamp is not selected, skip it
              continue;
            }

            // Check if this timestamp is visible (controls chart visibility, not selection)
            if (!matchingTimestamp.visible) {
              // Skip rendering this trace if not visible (user clicked sub-row to hide it)
              continue;
            }
          } else if (selectedTimestampsSet) {
            // No expandable series, but we're in focus mode - use selectedTimestampsSet directly
            const selectedTimestampValues = Array.from(selectedTimestampsSet);

            let matchesTimestamp = false;
            if (isLatestChipValue) {
              // Match to Latest timestamp
              matchesTimestamp = selectedTimestampValues.some(
                (val: any) => val === '__latest__' || val === 'latest' || val === '0' || val === 'Closest'
              );
            } else {
              // Match by ISO timestamp string with timezone-aware comparison
              matchesTimestamp = selectedTimestampValues.includes(sensor.chip_value);

              // If no exact match, try comparing as Date objects to handle timezone differences
              if (!matchesTimestamp) {
                try {
                  const chipDate = new Date(sensor.chip_value);
                  const chipTime = chipDate.getTime();

                  matchesTimestamp = selectedTimestampValues.some((selectedValue: any) => {
                    try {
                      const selectedDate = new Date(selectedValue);
                      const selectedTime = selectedDate.getTime();
                      return chipTime === selectedTime;
                    } catch {
                      return false;
                    }
                  });
                } catch {
                  matchesTimestamp = false;
                }
              }
            }

            if (!matchesTimestamp) {
              // This specific timestamp is not selected, skip it
              continue;
            }

            // No visibility check for sensors without expandable series
            // (visibility is only controlled through expandable legend)
          }
        } else if (sensor.chip_value && !shouldFilterByTimestamp) {
          // No timestamps are selected yet - use default color from sensor or first timestamp
          if (allTimestamps.length > 0) {
            matchingTimestamp = allTimestamps[0]; // Use first timestamp's color as default
          }
        } else if (!sensor.chip_value && shouldFilterByTimestamp) {
          // No chip_value but timestamps are enabled
          // Check if Latest is selected - if so, show this sensor's base data
          if (seriesForSensor && selectedTimestamps.length > 0) {
            const hasLatest = selectedTimestamps.some(
              (t: any) => t.id === '__latest__' || t.id === 'latest' || t.id === '0' || t.label === 'Latest'
            );
            if (!hasLatest) {
              // Only specific timestamps selected, skip base sensor without chip_value
              continue;
            }
          } else if (selectedTimestampsSet) {
            const selectedTimestampValues = Array.from(selectedTimestampsSet);
            const hasLatest = selectedTimestampValues.some(
              (val: any) => val === '__latest__' || val === 'latest' || val === '0' || val === 'Closest'
            );
            if (!hasLatest) {
              // Only specific timestamps selected, skip base sensor without chip_value
              continue;
            }
          } else {
            // No timestamp filtering can be done, skip
            continue;
          }
        }

        // Comprehensive validation
        if (!sensor || !Array.isArray(sensor.x) || !Array.isArray(sensor.y)) {
          continue;
        }

        // Validate data length matches timestamps
        if (sensor.y.length !== sensor.x.length) {
          continue;
        }

        // Validate data contains valid numbers
        const validDataCount = sensor.y.filter(
          (val: any) => typeof val === 'number' && !isNaN(val) && isFinite(val)
        ).length;

        if (validDataCount === 0) {
          continue;
        }

        // Get the color - in spectrum focus mode, use marker color from vertical line
        // This ensures each series matches its sub-row color (marker color)
        const labelIndex = labels.indexOf(label);

        // In multi-timestamp focus mode, ALWAYS use matchingTimestamp color (don't let sensor.color override it)
        // Check if we're in multi-timestamp mode by looking at either selectedTimestamps or selectedTimestampsSet
        const isMultiTimestampMode =
          shouldFilterByTimestamp &&
          (selectedTimestamps.length > 1 || (selectedTimestampsSet && selectedTimestampsSet.size > 1));
        let color: string;
        if (isMultiTimestampMode && matchingTimestamp?.color) {
          // Multi-timestamp focus: use assigned color from expandable legend (highest priority)
          color = matchingTimestamp.color;
        } else if (isMultiTimestampMode && sensor.chip_value && !seriesForSensor) {
          // Sensor without expandable series in multi-timestamp mode - find color from vertical lines
          const verticalLine = verticalLines?.find((line: any) => {
            const lineTimestamp =
              typeof line.timestamp === 'number'
                ? new Date(line.timestamp).toISOString()
                : line.timestamp.toISOString();
            return lineTimestamp === sensor.chip_value;
          });
          color = verticalLine?.color || sensor.color || colors[labelIndex] || colors[index] || '#000000';
        } else {
          // Single timestamp or no match: use sensor color or fallback
          color = matchingTimestamp?.color || sensor.color || colors[labelIndex] || colors[index] || '#000000';
        }

        // In focus mode, dim non-focused sensors instead of hiding them
        const shouldDimSensor = normalizedFocusedSensor && label !== normalizedFocusedSensor;
        let opacity = 1.0; // Default full opacity

        if (shouldDimSensor) {
          // Reduce opacity for non-focused sensors (visual dimming only)
          opacity = 0.1;
        }

        // Check if sensor has a type property (for alarms it should be 'bar')
        const chartType = sensor.type || 'scatter';
        const isBar = chartType === 'bar';

        // Process x-values based on detected axis type
        let processedX: any[];
        if (xAxisType === 'numeric') {
          // For numeric axis, keep values as numbers
          processedX = sensor.x.map((x: any) => (typeof x === 'number' ? x : Number(x)));
        } else if (xAxisType === 'date') {
          // For date axis, convert to Date objects
          processedX = sensor.x.map((ts: any) => (ts instanceof Date ? ts : new Date(ts)));
        } else {
          // For category axis, keep as-is
          processedX = sensor.x;
        }

        // For bar charts, use fixed width regardless of date spacing
        // In spectrum focus mode with multiple timestamps, use timestamp label as series name for unique identification
        const seriesName = matchingTimestamp ? matchingTimestamp.label : label;

        const barData: any = {
          x: processedX,
          y: sensor.y,
          type: chartType as any,
          mode: isBar ? undefined : isDiscrete ? 'lines+markers' : 'lines',
          name: seriesName,
        };

        // Convert color to rgba format if opacity is less than 1
        let finalColor = color;
        if (opacity < 1) {
          // Convert hex to rgba
          const hex = color.replace('#', '');
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          finalColor = `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }

        // Add bar-specific properties
        if (isBar) {
          // COMPLETELY FIXED WIDTH - same for all bars regardless of any data
          barData.width = 50000000; // Fixed 50 million milliseconds (~14 hours)
          barData.marker = {
            color: finalColor,
          };
        } else {
          barData.line = {
            color: finalColor,
            width: 1.3,
          };
        }

        // Add discrete marker properties
        if (isDiscrete) {
          barData.marker = {
            color: 'white',
            size: 8,
            symbol: 'circle',
            line: {
              width: 1.5,
              color: finalColor,
            },
          };
        }

        plotData.push({
          ...barData,
          // Hide default Plotly tooltip for all
          hoverinfo: 'none',
        });
      }
    } else {
      // NORMAL MODE: Loop through labels as before
      for (let index = 0; index < labels.length; index++) {
        const label = labels[index];
        if (!enabledSensors[label]) continue;

        // Check if we're in focus mode with timestamps enabled
        const seriesForSensor = expandableLegendSeries?.find((s: any) => s.label === label);

        // CRITICAL: In focus mode, apply timestamp filtering ONLY to spectrum charts, not SensorFusion
        // SensorFusion should only get visual highlighting (vertical lines), not data filtering
        const hasEnabledTimestamps =
          expandableLegendFocusMode && selectedTimestampsSet && selectedTimestampsSet.size > 0 && isSpectrum;

        // If timestamps are enabled, process ALL matching sensor entries (one per timestamp)
        // Otherwise, process only the first match (standard behavior)
        const matchingSensors = hasEnabledTimestamps
          ? data.data.filter((s: any) => s.displayName === label || s.name === label)
          : [data.data.find((s: any) => s.displayName === label || s.name === label)];

        for (const sensor of matchingSensors) {
          // Comprehensive validation
          if (!sensor || !Array.isArray(sensor.x) || !Array.isArray(sensor.y)) {
            continue;
          }

          // Filter by chip_value if timestamps are enabled
          if (hasEnabledTimestamps) {
            // Use selectedTimestampsSet directly - all sections use the same selected timestamps
            const selectedTimestampValues = Array.from(selectedTimestampsSet);

            // Check if this sensor's chip_value matches any enabled timestamp
            if (sensor.chip_value) {
              // Special handling for "Closest" / "Latest" / "0" variations
              const isLatestChipValue =
                sensor.chip_value === 'Closest' ||
                sensor.chip_value === '__latest__' ||
                sensor.chip_value === 'latest' ||
                sensor.chip_value === '0';

              let matchesTimestamp = false;
              if (isLatestChipValue) {
                // Match to Latest timestamp by checking if any Latest variation is in the set
                matchesTimestamp = selectedTimestampValues.some(
                  (val: any) => val === '__latest__' || val === 'latest' || val === '0' || val === 'Closest'
                );
              } else {
                // Match by ISO timestamp string with timezone-aware comparison
                // First try exact string match for performance
                matchesTimestamp = selectedTimestampValues.includes(sensor.chip_value);

                // If no exact match, try comparing as Date objects to handle timezone differences
                if (!matchesTimestamp) {
                  try {
                    const chipDate = new Date(sensor.chip_value);
                    const chipTime = chipDate.getTime();

                    matchesTimestamp = selectedTimestampValues.some((selectedValue: any) => {
                      try {
                        const selectedDate = new Date(selectedValue);
                        const selectedTime = selectedDate.getTime();
                        // Match if timestamps represent the same moment in time
                        return chipTime === selectedTime;
                      } catch {
                        return false;
                      }
                    });
                  } catch {
                    // If chip_value is not a valid date, fallback to string match
                    matchesTimestamp = false;
                  }
                }
              }

              if (!matchesTimestamp) {
                continue;
              }
            } else {
              // No chip_value but timestamps are enabled
              // IMPORTANT: Don't skip if Latest is selected - show the base sensor data
              const hasLatestSelected = selectedTimestampValues.some(
                (val: any) => val === '__latest__' || val === 'latest' || val === '0' || val === 'Closest'
              );
              if (!hasLatestSelected) {
                // Skip entries without chip_value when specific timestamps are selected
                continue;
              }
              // If Latest is selected, continue to show this sensor's data
            }
          }

          // Validate data length matches timestamps
          if (sensor.y.length !== sensor.x.length) {
            continue;
          }

          // Validate data contains valid numbers
          const validDataCount = sensor.y.filter(
            (val: any) => typeof val === 'number' && !isNaN(val) && isFinite(val)
          ).length;

          if (validDataCount === 0) {
            continue;
          }

          // Get the color - prefer sensor's own color property if available
          const labelIndex = labels.indexOf(label);
          let color = sensor.color || colors[labelIndex] || colors[index] || '#000000';

          // In focus mode, dim non-focused sensors instead of hiding them
          const shouldDimSensor = normalizedFocusedSensor && label !== normalizedFocusedSensor;
          let opacity = 1.0; // Default full opacity

          if (shouldDimSensor) {
            // Reduce opacity for non-focused sensors (visual dimming only)
            opacity = 0.1;
          }

          // Check if sensor has a type property (for alarms it should be 'bar')
          const chartType = sensor.type || 'scatter';
          const isBar = chartType === 'bar';

          // Process x-values based on detected axis type
          let processedX: any[];
          if (xAxisType === 'numeric') {
            // For numeric axis, keep values as numbers
            processedX = sensor.x.map((x: any) => (typeof x === 'number' ? x : Number(x)));
          } else if (xAxisType === 'date') {
            // For date axis, convert to Date objects
            processedX = sensor.x.map((ts: any) => (ts instanceof Date ? ts : new Date(ts)));
          } else {
            // For category axis, keep as-is
            processedX = sensor.x;
          }

          // For bar charts, use fixed width regardless of date spacing
          const barData: any = {
            x: processedX,
            y: sensor.y,
            type: chartType as any,
            mode: isBar ? undefined : isDiscrete ? 'lines+markers' : 'lines',
            name: label,
          };

          // Convert color to rgba format if opacity is less than 1
          let finalColor = color;
          if (opacity < 1) {
            // Convert hex to rgba
            const hex = color.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            finalColor = `rgba(${r}, ${g}, ${b}, ${opacity})`;
          }

          // Add bar-specific properties
          if (isBar) {
            // COMPLETELY FIXED WIDTH - same for all bars regardless of any data
            barData.width = 50000000; // Fixed 50 million milliseconds (~14 hours)
            barData.marker = {
              color: finalColor,
            };
          } else {
            barData.line = {
              color: finalColor,
              width: 1.3,
            };
          }

          // Add discrete marker properties
          if (isDiscrete) {
            barData.marker = {
              color: 'white',
              size: 8,
              symbol: 'circle',
              line: {
                width: 1.5,
                color: finalColor,
              },
            };
          }

          plotData.push({
            ...barData,
            // Hide default Plotly tooltip for all
            hoverinfo: 'none',
          });
        } // End of matchingSensors loop
      } // End of labels loop
    }
  } catch (error) {
    return (
      <div className="flex items-center justify-center h-48 bg-[#f0f2f7] rounded border">
        <div className="text-[#55687d]">
          <p className="text-center">Error preparing chart data</p>
          <p className="text-center text-xs mt-2">Check console for details</p>
        </div>
      </div>
    );
  }

  // Validate we have data to plot
  if (plotData.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px] bg-white dark:bg-[#52606d] rounded border border-gray-200 dark:border-[#2f3844]">
        <div className="flex flex-col items-center justify-center text-[#55687d] dark:text-[#8b98a5]">
          <BarChart3 className="w-12 h-12 mb-4 opacity-40" />
          <p className="text-center font-semibold">No valid data to display</p>
          <p className="text-center text-xs mt-2">Enable sensors with valid data</p>
        </div>
      </div>
    );
  }

  // All shapes, annotations, layout, and useMemo hooks are now defined BEFORE early returns (lines 1238-1433)

  // ***  Plot key includes transition state for proper re-rendering ***
  // IMPORTANT: benchmarkLineId removed from key to prevent zoom reset when benchmark changes
  // Benchmark only affects line styles (solid/dashed), not the chart structure
  // Spectrum: stable key keeps Plot mounted — Plotly.react() handles data updates, uirevision preserves zoom
  const plotKey = isSpectrum
    ? `${chartId}-spectrum`
    : `${chartId}-${chartComponentIdRef.current}-${dataVersion}-${Object.keys(enabledSensors)
        .filter((k) => enabledSensors[k])
        .join('-')}-${isModeTransition ? 'transition' : 'normal'}-${preservedTimeRange ? 'preserved' : 'default'}`;

  return (
    <div className="relative">
      {/* Floating Toolbar */}
      {showFloatingToolbar && (
        <FloatingChartToolbar
          onEnterMarkerSelectionMode={onEnterMarkerSelectionMode}
          isMarkerSelectionMode={isMarkerSelectionMode}
          markerLabel={markerLabel}
          harmonicSelections={harmonicSelections}
          onAddHarmonicSelection={onAddHarmonicSelection}
          onUpdateHarmonicSelection={onUpdateHarmonicSelection}
          onRemoveHarmonicSelection={onRemoveHarmonicSelection}
          onRemoveAllHarmonics={onRemoveAllHarmonics}
          onFocusHarmonic={onFocusHarmonic}
          onResetHarmonic={onResetHarmonic}
          onEnterHarmonicSelectionMode={onEnterHarmonicSelectionMode}
          onGenerateHarmonics={onGenerateHarmonics}
          isHarmonicSelectionMode={isHarmonicSelectionMode}
          sideBandsSelections={sideBandsSelections}
          onEnterSideBandsSelectionMode={onEnterSideBandsSelectionMode}
          isSideBandsSelectionMode={isSideBandsSelectionMode}
          verticalLines={verticalLines}
          showComparisonTable={showComparisonTable}
          onToggleCompareModal={onToggleCompareModal}
          onClearLines={onClearLines}
          xAxisType={xAxisType}
          pendingHarmonicCreation={pendingHarmonicCreation}
          markingTools={markingTools}
        />
      )}

      <Plot
        key={plotKey} // ***  Enhanced key for proper re-rendering ***
        ref={plotRef}
        data={plotData} //!
        layout={layout} //!
        config={plotConfig}
        onClick={(event: any) => {
          // Skip click handling when drawing mode is active
          if (isDrawingActive) return;
          if (!selectionMode && handleClick) {
            handleClick(event);
          }
        }}
        onSelected={selectionMode ? handleSelection : undefined}
        onClickAnnotation={isDrawingActive ? undefined : handleAnnotationClick}
        onHover={isDrawingActive ? undefined : handleHover}
        onUnhover={isDrawingActive ? undefined : handleUnhover}
        style={{ width: '100%', height: `${height}px` }}
        divId={`chart-${chartId}`}
        useResizeHandler={true}
        onRelayout={(figure: any) => {
          try {
            if (selectionMode) return false;

            // Prevent recursive updates
            if (isUpdatingRef.current) {
              return false;
            }

            const dataStart = new Date(data.start_datetime);
            const dataEnd = new Date(data.end_datetime);

            // *** CRITICAL: Block all relayout events during mode transitions ***
            if (isModeTransition) {
              // Block relayout during mode transition
              return false;
            }

            // Improved double-click reset detection
            const isDoubleClickReset =
              figure['xaxis.autorange'] === true ||
              figure['yaxis.autorange'] === true ||
              (figure['xaxis.range[0]'] === undefined &&
                figure['xaxis.range[1]'] === undefined &&
                figure['yaxis.range[0]'] === undefined &&
                figure['yaxis.range[1]'] === undefined) ||
              Object.keys(figure).some((key) => key.includes('autorange'));

            if (isDoubleClickReset) {
              // Spectrum: let Plotly handle autoscale natively — skip cascade that overrides zoom
              if (isSpectrum) return;
              // Double-click reset detected - reset both X and Y axes
              isResettingRef.current = true;
              isUpdatingRef.current = true;

              // Reset both internal state and preserved range
              setInternalTimeRange(null);
              hasAppliedPreservedRangeRef.current = false;

              // Calculate Y-range for reset
              let resetYRange: [number, number] | null = null;

              // Helper to find min/max without spread (avoids stack overflow on large arrays)
              const findMinMax = (arr: number[]): [number, number] | null => {
                if (arr.length === 0) return null;
                let min = Number.POSITIVE_INFINITY;
                let max = Number.NEGATIVE_INFINITY;
                for (let i = 0; i < arr.length; i++) {
                  const val = arr[i];
                  if (val < min) min = val;
                  if (val > max) max = val;
                }
                return isFinite(min) && isFinite(max) ? [min, max] : null;
              };

              if (isSplitChart) {
                // For split charts, recalculate individual Y-range
                const sensor = data.data?.find((s: any) => s.displayName === sensorName);
                if (sensor && Array.isArray(sensor.y)) {
                  const validData = sensor.y.filter(
                    (val: any) => typeof val === 'number' && !isNaN(val) && isFinite(val)
                  );
                  const minMax = findMinMax(validData);
                  if (minMax) {
                    const [localMin, localMax] = minMax;
                    const padding = Math.max((localMax - localMin) * 0.1, 1);
                    resetYRange = [localMin - padding, localMax + padding];
                  }
                }
              } else {
                // For combined charts, recalculate for all enabled sensors
                let resetMinY = Number.POSITIVE_INFINITY;
                let resetMaxY = Number.NEGATIVE_INFINITY;

                labels.forEach((label) => {
                  if (!enabledSensors[label]) return;

                  const sensor = data.data?.find((s: any) => s.displayName === label);
                  if (!sensor || !Array.isArray(sensor.y)) return;

                  const validData = sensor.y.filter(
                    (val: any) => typeof val === 'number' && !isNaN(val) && isFinite(val)
                  );

                  const minMax = findMinMax(validData);
                  if (minMax) {
                    const [localMin, localMax] = minMax;
                    resetMinY = Math.min(resetMinY, localMin);
                    resetMaxY = Math.max(resetMaxY, localMax);
                  }
                });

                if (resetMinY !== Number.POSITIVE_INFINITY && resetMaxY !== Number.NEGATIVE_INFINITY) {
                  const padding = Math.max((resetMaxY - resetMinY) * 0.1, 1);
                  resetYRange = [resetMinY - padding, resetMaxY + padding];
                }
              }

              // Reset to full data range through parent
              onRangeChange([dataStart, dataEnd]);

              // Apply Y-axis reset
              setTimeout(() => {
                const plotElement = plotRef.current?.el;
                if (plotElement && (window as any).Plotly) {
                  const yAxisUpdate: Record<string, any> = resetYRange
                    ? {
                        'yaxis.range': resetYRange,
                        'yaxis.autorange': false,
                        'yaxis.fixedrange': false,
                      }
                    : { 'yaxis.autorange': true };

                  (window as any).Plotly.relayout(plotElement, yAxisUpdate)
                    .then(() => {
                      // Y-axis reset completed successfully
                      // Update state to match the reset
                      if (resetYRange) {
                        setOriginalYRange(resetYRange);
                        setMinY(resetYRange[0]);
                        setMaxY(resetYRange[1]);
                      }
                      isUpdatingRef.current = false;
                      isResettingRef.current = false;
                    })
                    .catch((error: any) => {
                      isUpdatingRef.current = false;
                      isResettingRef.current = false;
                    });
                } else {
                  isUpdatingRef.current = false;
                  isResettingRef.current = false;
                }
              }, 10);

              return false; // Prevent Plotly from handling the reset
            }

            // Handle X-axis range changes - normal zoom/pan operations
            // Only apply date-based range handling for date-based charts (skip for spectrum/numeric charts)
            if (xAxisType === 'date') {
              const xStart = figure['xaxis.range[0]'] ? new Date(figure['xaxis.range[0]']) : null;
              const xEnd = figure['xaxis.range[1]'] ? new Date(figure['xaxis.range[1]']) : null;

              if (xStart && xEnd) {
                // Validate range is within data bounds
                if (xStart < dataStart || xEnd > dataEnd) {
                  isUpdatingRef.current = true;
                  const clampedStart = xStart < dataStart ? dataStart : xStart;
                  const clampedEnd = xEnd > dataEnd ? dataEnd : xEnd;

                  // Clamp out-of-bounds range
                  onRangeChange([clampedStart, clampedEnd]);

                  setTimeout(() => {
                    isUpdatingRef.current = false;
                  }, 100);
                  return false;
                }

                // Normal range change - propagate to parent
                isUpdatingRef.current = true;
                // Normal range change
                onRangeChange([xStart, xEnd]);

                setTimeout(() => {
                  isUpdatingRef.current = false;
                }, 100);
              }
            }

            // Allow Y-axis changes during normal operation
            if (
              !isResettingRef.current &&
              (figure['yaxis.range[0]'] !== undefined ||
                figure['yaxis.range[1]'] !== undefined ||
                figure['yaxis.autorange'] !== undefined)
            ) {
              return true;
            }

            return true;
          } catch (error) {
            isUpdatingRef.current = false;
            isResettingRef.current = false;
            return false;
          }
        }}
        onInitialized={(figure, graphDiv) => addPlotlyTestIds(graphDiv)}
      />

      {/* Drawing Canvas Overlay */}
      {drawingContext && <DrawingCanvas chartId={chartId} plotRef={plotRef} xAxisType={xAxisType} />}

      {/* Custom Popover */}
      <ChartPopover
        isVisible={popoverData.isVisible}
        position={popoverData.position}
        data={popoverData.data}
        showTooltipData={showTooltipData}
        renderTooltipData={renderTooltipData}
      />

      {/* Cursor Label - shows active marking mode - positioned within chart */}
      {cursorPosition && !showComparisonTable && (
        <div
          style={{
            position: 'absolute',
            left: `${cursorPosition.x + 15}px`,
            top: `${popoverData.isVisible ? cursorPosition.y + 80 : cursorPosition.y - 40}px`,
            zIndex: 9999,
            pointerEvents: 'none',
          }}
          className="px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-md shadow-lg whitespace-nowrap"
        >
          {(() => {
            const label = isPendingMode
              ? `${
                  pendingHarmonicCreation?.type === 'sideBands' ? 'Side Bands' : 'Harmonics'
                } Mode: Click to place second point`
              : activeCustomLineMode
              ? `${customLineGroups[activeCustomLineMode]?.groupIcon || ''} ${
                  customLineGroups[activeCustomLineMode]?.groupLabel || 'Custom'
                } Mode: Click to add ${customLineGroups[activeCustomLineMode]?.groupLabel?.toLowerCase() || 'line'}`
              : isCustomLineMode
              ? `Custom Line Mode: Click to add line`
              : isMarkerSelectionMode
              ? `${markerLabel} Mode: Click to add ${markerLabel.toLowerCase()}`
              : isHarmonicSelectionMode
              ? 'Harmonics Mode: Click to place starting point'
              : isSideBandsSelectionMode
              ? 'Sidebands Mode: Click to place starting point'
              : '';
            return label;
          })()}
        </div>
      )}

      {/* Custom Vertical Line Menu */}
      <CustomVerticalLineMenu
        open={customLineMenu?.isOpen || false}
        onClose={() => setCustomLineMenu(null)}
        onFocusGroup={() => {
          const groupId = customLineMenu?.lineData?.groupId;
          if (groupId) {
            setLocalFocusedCustomGroupId(groupId);
          }
        }}
        onUnfocusGroup={() => {
          setLocalFocusedCustomGroupId(null);
        }}
        isFocused={localFocusedCustomGroupId === customLineMenu?.lineData?.groupId}
        onSaveToDatabase={() => {
          // TODO: Implement save to database
        }}
        onRemove={() => {
          // TODO: Implement remove line
        }}
        onDim={() => {
          const lineId = customLineMenu?.lineData?.id;
          if (!lineId) return;
          const line = allVerticalLines.find((l) => l.id === lineId);
          if (!line) return;
          const newDimmed = !line.dimmed;

          // Check if this is a custom line (from customVerticalLinesState)
          if (line.isCustomLine) {
            // Update the line in customVerticalLinesState
            setCustomVerticalLinesState((prev: any) => {
              if (!prev?.groups) return prev;
              return {
                ...prev,
                groups: prev.groups.map((group: any) => ({
                  ...group,
                  groupItems:
                    group.groupItems?.map((item: any) =>
                      item.id === lineId ? { ...item, dimmed: newDimmed } : item
                    ) || [],
                })),
              };
            });
          } else if (onUpdateVerticalLine) {
            // Regular line - use the update function
            onUpdateVerticalLine(lineId, { dimmed: newDimmed });
          }
        }}
        onDimGroup={() => {
          const groupId = customLineMenu?.lineData?.groupId;
          if (!groupId) return;
          // Find all lines in this group
          const groupLines = allVerticalLines.filter(
            (l) => l.groupId === groupId || (l as any)._systemGroupId === groupId
          );
          if (groupLines.length === 0) return;
          // Check if ANY line in group is dimmed - if so, undim all; otherwise dim all
          // This matches the display logic: groupDimmed = some((l) => l.dimmed)
          // User expectation: if group was dimmed and one was undimmed, "Undim Group" should clear all dimming
          const anyDimmed = groupLines.some((l) => l.dimmed);
          const newDimmedValue = !anyDimmed; // If any dimmed → undim all (false); if none dimmed → dim all (true)

          // Separate custom lines and regular lines
          const customLines = groupLines.filter((l) => l.isCustomLine);
          const regularLines = groupLines.filter((l) => !l.isCustomLine);

          // Update custom lines in customVerticalLinesState
          if (customLines.length > 0) {
            setCustomVerticalLinesState((prev: any) => {
              if (!prev?.groups) return prev;
              const customLineIds = new Set(customLines.map((l) => l.id));
              return {
                ...prev,
                groups: prev.groups.map((group: any) => ({
                  ...group,
                  groupItems:
                    group.groupItems?.map((item: any) =>
                      customLineIds.has(item.id) ? { ...item, dimmed: newDimmedValue } : item
                    ) || [],
                })),
              };
            });
          }

          // Update regular lines via onUpdateVerticalLine
          if (regularLines.length > 0 && onUpdateVerticalLine) {
            regularLines.forEach((line) => {
              onUpdateVerticalLine(line.id, { dimmed: newDimmedValue });
            });
          }
        }}
        onEdit={() => {
          // TODO: Implement edit label
        }}
        position={customLineMenu?.position}
        lineData={
          customLineMenu?.lineData
            ? {
                ...customLineMenu.lineData,
                // Compute groupDimmed: true if ANY line in the group is dimmed
                // User expectation: if some lines are still dimmed, show "Undim Group" to clear all dimming
                groupDimmed: (() => {
                  const groupId = customLineMenu?.lineData?.groupId;
                  if (!groupId) return false;
                  const groupLines = allVerticalLines.filter(
                    (l) => l.groupId === groupId || (l as any)._systemGroupId === groupId
                  );
                  return groupLines.length > 0 && groupLines.some((l) => l.dimmed);
                })(),
              }
            : undefined
        }
        canRemove={customLineMenu?.lineData?.source !== 'system'}
      />

      {/* Custom Line Tooltip (Netflix-style on double-click) */}
      <CustomLineTooltip
        visible={customLineTooltip.visible}
        x={customLineTooltip.x}
        y={customLineTooltip.y}
        lineData={customLineTooltip.lineData}
      />
    </div>
  );
};

const addPlotlyTestIds = (graphDiv: HTMLElement) => {
  const buttons = graphDiv.querySelectorAll('.modebar-btn');
  buttons.forEach((btn, index) => {
    const title = btn.getAttribute('data-title') || `button-${index}`;
    const testId = `icon_${generateTestId(title)}`;
    btn.setAttribute('data-testid', testId);
  });
};

// Memoize the PlotlyChart to prevent unnecessary re-renders
export const PlotlyChart = React.memo(PlotlyChartComponent);
